import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
  Dimensions,
  useWindowDimensions,
  SafeAreaView,
  StatusBar,
  ImageBackground,
  Platform,
  Modal,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Animated,
  Easing,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FontAwesome5, Ionicons } from '@expo/vector-icons';
import { Image } from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import { useFonts } from 'expo-font';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { fetchUserProfile, doLogout } from '../services/userDataService';
import { getShippingAddresses } from '../services/shippingAddressApi';
import SpinWheelShippingAddressModal from '../components/SpinWheelShippingAddressModal';
import { getPromoCornerData, isPromoVoucherCouponKegunaan } from '../services/promoCornerApi';
import { scanQrCode, transformScanResult, validateQrCode, transformValidateResult } from '../services/qrScanApi';
import { getPaygiftClaimModalUi } from '../utils/paygiftUserFacingMessage';
import { usePaygiftClaimBlockedModal } from '../components/PaygiftClaimBlockedModal';
import { API_BASE_URL } from '../config/api';
import {
  getReferralAvailable,
  joinPromotion,
  normalizeReferralPromotions,
  referralOptionsAfterScan,
  shouldOfferPolReferralPopup,
} from '../services/referralApi';
import {
  navigateToClaimedCouponDetails,
  shouldDirectToCouponAfterKawanClaim,
  shouldNavigateToCouponDetails,
} from '../navigation/navigateToClaimedCoupon';
import {
  getActiveSpinningWheel,
  postSpinningWheel,
  postSpinWheelClaimProduct,
  getPendingSpinProductClaims,
} from '../services/spinningWheelApi';
import { resolveSpinWheelTheme, spinButtonGradientColors } from '../utils/spinWheelTheme';
import { getSpinProductDisplayLines } from '../utils/spinProductDisplay';
import { useFloatingButton } from '../contexts/FloatingButtonContext';
import Svg, {
  G, Path, Circle as SvgCircle, Rect as SvgRect,
  Defs, LinearGradient as SvgLinearGradient, Stop,
  Text as SvgText,
} from 'react-native-svg';
import { formatIGBannerForDisplay, getIGBanner } from '../services/bannerApi';
import { HeroOutline, HeroSolid, HeroSolid20 } from '@nandorojo/heroicons';
import { getLatestPendingQR, deletePendingQR, getPendingQRs, resetPendingQRs } from '../utils/pendingQR';
import {
  consumePendingSocialBannerId,
  hydratePendingSocialBannerId,
  peekPendingSocialBannerId,
} from '../utils/socialBannerLink';
import ScanClaimPrizeList from '../components/ScanClaimPrizeList';
import ScanSuccessNextActions from '../components/ScanSuccessNextActions';
import NotificationBellButton from '../components/NotificationBellButton';
import { formatPoinNumber, qrRewardSourceName, scanPointsFromLabel, scanStampsFromLabel, isPointsScanSuccessWithoutCoupon, PointRewardHint, useConfettiAndClap } from '../utils/scanClaimUi';
import SocialBannerFeed from '../components/promoSocial/SocialBannerFeed';
import notificationApi from '../services/notificationApi';
import { useBannerWithPopUp } from '../components/BannerWithPopUp';

const { width, height } = Dimensions.get('window');

// Wheel geometry (246 × 246)
const WHEEL_SIZE   = 246;
const WHEEL_CX     = WHEEL_SIZE / 2;  // 123
// Fallback logo social box jika program tidak menetapkan logo khusus
const SOCIAL_BOX_LOGO_FALLBACK = `${API_BASE_URL}/assets/img/logo-zoolyfe.png`;
const SELAMAT_BANNER_URI = `${API_BASE_URL}/assets/img/selamat-icon.svg`;
const POPUP_WHEEL_SIZE   = WHEEL_SIZE;
const POPUP_WHEEL_RADIUS = WHEEL_SIZE / 2 - 8;

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function mod360Spin(x) {
  const v = Number(x) || 0;
  const m = v % 360;
  return m < 0 ? m + 360 : m;
}

function parsePrizeIndexSpin(raw, maxIdx) {
  let i = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isNaN(i) || i < 0) i = 0;
  if (typeof maxIdx === 'number' && maxIdx >= 0 && i > maxIdx) i = maxIdx;
  return i;
}

function slicePath(centerX, centerY, radius, startAngle, endAngle) {
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return [
    'M',
    centerX,
    centerY,
    'L',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
    'Z',
  ].join(' ');
}

/* ─── Wheel geometry constants ─────────────────────────────────────────── */
const W_OUTER_R  = WHEEL_CX;          // 123 – background circle
const W_DOTS_R   = WHEEL_CX - 5;      // 118 – dot ring (tengah area rim)
const W_RING1_R  = WHEEL_CX - 11;     // 112 – outer white stroke
const W_GOLD_R   = WHEEL_CX - 14;     // 109 – gold stroke (strokeWidth 5)
const W_SLICE_R  = WHEEL_CX - 16;     // 107 – slice outer edge (langsung setelah gold ring)
const W_LABEL_R  = 66;                //       label radial distance (base; more slices → outer)
const W_CENTER_R = 15;                //       center hub (small)

function resolveSpinLogoSize(value) {
  return String(value || '').toLowerCase() === 'large' ? 'large' : 'small';
}

/** Center logo + hub geometry, scales with rendered wheel size. */
function wheelLogoLayout(logoSize, wheelSize = WHEEL_SIZE) {
  const scale = wheelSize / WHEEL_SIZE;
  if (logoSize === 'large') {
    return {
      wrap: Math.round(132 * scale),
      logo: Math.round(112 * scale),
      hubR: Math.round(66 * scale),
      borderW: Math.max(1, Math.round(3 * scale)),
    };
  }
  return {
    wrap: Math.round(24 * scale),
    logo: Math.round(20 * scale),
    hubR: Math.round(W_CENTER_R * scale),
    borderW: Math.max(1, Math.round(2 * scale)),
  };
}

function sliceCustomText(p) {
  return String(p?.slice_display_text || '').trim();
}

/** Radial center of the slice band (between hub edge and outer rim). */
function sliceLabelRadius(n, logoSize = 'small') {
  const sizeKey = resolveSpinLogoSize(logoSize);
  const hubR = wheelLogoLayout(sizeKey, WHEEL_SIZE).hubR;
  const padIn = sizeKey === 'large' ? 6 : 10;
  const padOut = 12;
  const inner = hubR + padIn;
  const outer = W_SLICE_R - padOut;
  const mid = Math.round((inner + outer) / 2);
  const count = Math.max(1, n);
  if (count >= 9) return Math.min(outer - 2, mid + 4);
  if (count >= 7) return Math.min(outer - 2, mid + 2);
  return mid;
}

function sliceArcLen(n, logoSize = 'small') {
  return (2 * Math.PI * sliceLabelRadius(n, logoSize)) / Math.max(1, n);
}

function sliceMaxLines(n) {
  return n >= 7 ? 3 : 2;
}

function sliceFontSize(n, logoSize = 'small') {
  const count = Math.max(1, n);
  const arc = sliceArcLen(count, logoSize);
  const fs = Math.floor(arc * 0.36);
  if (count >= 8) return Math.max(9, Math.min(11, fs));
  if (count >= 6) return Math.max(8, Math.min(12, fs));
  return Math.max(6, Math.min(14, fs));
}

function sliceMaxCharsPerLine(n, fontSize, logoSize = 'small') {
  const arc = sliceArcLen(n, logoSize);
  const chars = Math.floor((arc * 0.58) / Math.max(4.5, fontSize * 0.48));
  if (n >= 8) return Math.max(6, Math.min(10, chars));
  if (n >= 6) return Math.max(5, Math.min(12, chars));
  return Math.max(3, Math.min(14, chars));
}

function sliceWrapLines(text, maxPerLine, maxLines = 2) {
  const t = String(text || '').trim();
  if (!t) return [];
  const lines = [];
  t.split(/\n+/).forEach((paragraph) => {
    const words = String(paragraph || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length) return;
    let cur = '';
    words.forEach((word) => {
      const next = cur ? `${cur} ${word}` : word;
      if (next.length <= maxPerLine) {
        cur = next;
      } else {
        if (cur) lines.push(cur);
        cur = word;
      }
    });
    if (cur) lines.push(cur);
  });
  return lines.slice(0, maxLines);
}

function WheelSliceDefaultIcon({ prize, index, sliceCount, theme, gradPrefix = 'sw', logoSize = 'small' }) {
  const n = Math.max(1, sliceCount);
  const th = resolveSpinWheelTheme(theme);
  if (prize.prize_type === 'points' && (prize.points_value || 0) > 0) {
    const fs = Math.max(12, Math.min(18, sliceFontSize(n, logoSize)));
    return (
      <G>
        <SvgCircle cx={-11} cy={0} r={3.75} fill="#FEF3C7" stroke={th.slice_icon_star} strokeWidth={0.6} />
        <SvgText x={-11} y={1.5} textAnchor="middle" fill={th.slice_icon_points} fontSize={4} fontWeight="700">◉</SvgText>
        <SvgText
          x={-3}
          y={fs * 0.35}
          textAnchor="start"
          fill={`url(#${gradPrefix}ptsGrad)`}
          fontSize={fs}
          fontWeight="700"
          fontFamily="Poppins"
        >
          {prize.points_value}
        </SvgText>
      </G>
    );
  }
  if (prize.prize_type === 'product') {
    const scale = n > 8 ? 0.95 : 1.2;
    return (
      <G transform={`translate(0, -2) scale(${scale})`}>
        <SvgRect x={-10} y={-1} width={20} height={14} rx={2} fill="#FCD34D" />
        <SvgRect x={-11} y={-7} width={22} height={7} rx={2} fill="#F59E0B" />
        <SvgRect x={-2} y={-7} width={4} height={21} fill="rgba(255,255,255,0.45)" />
        <SvgRect x={-11} y={2} width={22} height={4} fill="rgba(255,255,255,0.45)" />
        <SvgCircle cx={-6} cy={-7} r={3.5} fill="#FBBF24" />
        <SvgCircle cx={6} cy={-7} r={3.5} fill="#FBBF24" />
      </G>
    );
  }
  const starFs = Math.max(14, Math.min(24, Math.floor(200 / n)));
  return (
    <SvgText
      x={0}
      y={starFs * 0.35}
      textAnchor="middle"
      fill={prize.prize_type === 'voucher' ? `url(#${gradPrefix}starGrad)` : (index % 2 === 0 ? th.slice_icon_star : th.slice_icon_star_alt)}
      fontSize={starFs}
      fontWeight="700"
    >
      ◉
    </SvgText>
  );
}

function WheelSliceCustomText({ text, index, sliceCount, theme, prize, logoSize = 'small' }) {
  const n = Math.max(1, sliceCount);
  const th = resolveSpinWheelTheme(theme);
  const fs = sliceFontSize(n, logoSize);
  const maxChars = sliceMaxCharsPerLine(n, fs, logoSize);
  const lines = sliceWrapLines(text, maxChars, sliceMaxLines(n));
  const lh = fs * 1.1;
  const startY = -((lines.length - 1) * lh) / 2;
  const fill = index % 2 === 0 ? th.slice_label_on_a : th.slice_label_on_b;
  return (
    <G>
      {lines.map((line, li) => (
        line ? (
          <SvgText
            key={`ln-${li}`}
            x={0}
            y={startY + li * lh}
            textAnchor="middle"
            fill={fill}
            fontSize={fs}
            fontWeight="600"
            fontFamily="Poppins"
          >
            {line}
          </SvgText>
        ) : null
      ))}
    </G>
  );
}

/* ─── Countdown timer to Jakarta midnight (resets daily spin) ─── */
function CountdownTimer({ style }) {
  const calc = () => {
    const JKT_OFFSET = 7 * 3600;
    const jakartaSec = Date.now() / 1000 + JKT_OFFSET;
    const nextMidSec = (Math.floor(jakartaSec / 86400) + 1) * 86400;
    const rem = Math.max(0, Math.round(nextMidSec - jakartaSec));
    const h = Math.floor(rem / 3600);
    const m = Math.floor((rem % 3600) / 60);
    const s = rem % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  const [time, setTime] = React.useState(calc);
  React.useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return <Text style={style}>{time}</Text>;
}

/* ─── Banner SELAMAT (asset zoolife) ─── */
function PrizeSelamatBanner() {
  return (
    <Image
      source={{ uri: SELAMAT_BANNER_URI }}
      style={{ width: '100%', maxWidth: 252, aspectRatio: 252 / 91 }}
      resizeMode="contain"
      accessibilityLabel="Selamat"
    />
  );
}

const PRIZE_STAR_CORE = 45;
const PRIZE_STAR_RING1 = PRIZE_STAR_CORE + 12;
const PRIZE_STAR_RING2 = PRIZE_STAR_RING1 + 12;

function PrizePointsStarRings() {
  return (
    <View style={{ width: PRIZE_STAR_RING2, height: PRIZE_STAR_RING2, alignItems: 'center', justifyContent: 'center' }}>
      <View
        style={{
          position: 'absolute',
          width: PRIZE_STAR_RING2,
          height: PRIZE_STAR_RING2,
          borderRadius: PRIZE_STAR_RING2 / 2,
          backgroundColor: '#FEF3C7',
        }}
      />
      <View
        style={{
          position: 'absolute',
          width: PRIZE_STAR_RING1,
          height: PRIZE_STAR_RING1,
          borderRadius: PRIZE_STAR_RING1 / 2,
          backgroundColor: '#FDE68A',
        }}
      />
      <LinearGradient
        colors={['#FBBF24', '#F59E0B']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          width: PRIZE_STAR_CORE,
          height: PRIZE_STAR_CORE,
          borderRadius: PRIZE_STAR_CORE / 2,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PointIcon size={22} color={"#FFFFFF"} />
      </LinearGradient>
    </View>
  );
}

function SpinResultProductTitle({ displayName, label }) {
  const gradId = 'spinProdTitleGrad';
  const safe = String(displayName || label || 'Hadiah').trim();
  const vbw = Math.min(340, Math.max(72, safe.length * 9));
  const mid = vbw / 2;
  return (
    <View style={{ width: '100%', alignItems: 'center', minHeight: 22 }}>
      <Svg width="100%" height={24} viewBox={`0 0 ${vbw} 24`} preserveAspectRatio="xMidYMid meet">
        <Defs>
          <SvgLinearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <Stop offset="0%" stopColor="#FBBF24" />
            <Stop offset="100%" stopColor="#F59E0B" />
          </SvgLinearGradient>
        </Defs>
        <SvgText
          x={mid}
          y={17}
          textAnchor="middle"
          fill={`url(#${gradId})`}
          fontSize={14}
          fontWeight="600"
          fontFamily="Poppins"
        >
          {safe}
        </SvgText>
      </Svg>
    </View>
  );
}

function PrizePointsGradientLabel({ label }) {
  const gradId = 'prizePtsGradLbl';
  return (
    <Svg width={280} height={26} viewBox="0 0 280 26">
      <Defs>
        <SvgLinearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <Stop offset="0%" stopColor="#FBBF24" />
          <Stop offset="100%" stopColor="#F59E0B" />
        </SvgLinearGradient>
      </Defs>
      <SvgText
        x={140}
        y={18}
        textAnchor="middle"
        fill={`url(#${gradId})`}
        fontSize={14}
        fontWeight="600"
        fontFamily="Poppins"
      >
        {label}
      </SvgText>
    </Svg>
  );
}

/* ─── Animated dots "... Memutar" shown while spinning ─── */
function AnimatedDots() {
  const [dots, setDots] = React.useState('.');
  React.useEffect(() => {
    const id = setInterval(() => {
      setDots(d => (d.length >= 3 ? '.' : d + '.'));
    }, 400);
    return () => clearInterval(id);
  }, []);
  return (
    <Text style={{ color: '#FFFFFF', fontSize: 14, fontWeight: '500', fontFamily: 'Poppins-Medium' }}>
      {dots} Memutar
    </Text>
  );
}

/* ─── Pointer — sama dengan assets/img/pointer.svg (RN tidak render .svg via Image) ─── */
function PointerSvg({ theme, idPrefix = 'spinWheelPtr' }) {
  const t = resolveSpinWheelTheme(theme);
  const fillId = `${idPrefix}Fill`;
  const strokeId = `${idPrefix}Stroke`;
  return (
    <Svg width={32} height={43} viewBox="0 0 32 43">
      <Defs>
        <SvgLinearGradient
          id={fillId}
          x1={15.6968}
          y1={39}
          x2={15.6968}
          y2={1.5}
          gradientUnits="userSpaceOnUse"
        >
          <Stop offset="0%" stopColor={t.pointer_fill_from} />
          <Stop offset="100%" stopColor={t.pointer_fill_to} />
        </SvgLinearGradient>
        <SvgLinearGradient
          id={strokeId}
          x1={15.6968}
          y1={-0.919348}
          x2={15.6968}
          y2={39}
          gradientUnits="userSpaceOnUse"
        >
          <Stop offset="0%" stopColor={t.pointer_stroke_from} />
          <Stop offset="100%" stopColor={t.pointer_stroke_to} />
        </SvgLinearGradient>
      </Defs>
      <Path
        d="M10.502 1.5L20.8916 1.5C22.8316 1.5 24.2615 3.31365 23.8088 5.20013L18.6139 26.8452C17.878 29.9117 13.5156 29.9117 12.7796 26.8452L7.58481 5.20012C7.13205 3.31364 8.56192 1.5 10.502 1.5Z"
        fill={`url(#${fillId})`}
      />
      <Path
        d="M19.3433 27.0205C18.4231 30.8533 12.9704 30.8532 12.0503 27.0205L6.85596 5.375C6.29005 3.01692 8.0774 0.749999 10.5024 0.749999L20.8911 0.75C23.3162 0.75 25.1035 3.01692 24.5376 5.375L19.3433 27.0205Z"
        fill="none"
        stroke={`url(#${strokeId})`}
        strokeWidth={1.5}
      />
    </Svg>
  );
}

/* ─── WheelSvg component ────────────────────────────────────────────────── */
function WheelSvg({ prizes, size = WHEEL_SIZE, theme, gradPrefix = 'sw', logoSize = 'small' }) {
  const th = resolveSpinWheelTheme(theme);
  const n     = prizes.length;
  const sdeg  = n > 0 ? 360 / n : 0;
  const cx    = WHEEL_CX;
  const cy    = WHEEL_CX;
  const gid = (s) => `${gradPrefix}${s}`;
  const sizeKey = resolveSpinLogoSize(logoSize);
  const hubR = wheelLogoLayout(sizeKey, size).hubR;

  const dots = n > 0 ? Array.from({ length: n }, (_, i) => {
    const midDeg = i * sdeg + sdeg / 2;
    const a = (midDeg - 90) * Math.PI / 180;
    return { x: cx + W_DOTS_R * Math.cos(a), y: cy + W_DOTS_R * Math.sin(a), gold: i % 2 === 0 };
  }) : [];

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}>
      <Defs>
        <SvgLinearGradient id={gid('wBg')} x1="0%" y1="0%" x2="100%" y2="100%">
          <Stop offset="0%" stopColor={th.wheel_bg_from} />
          <Stop offset="100%" stopColor={th.wheel_bg_to} />
        </SvgLinearGradient>
        <SvgLinearGradient id={gid('wGold')} x1="0%" y1="0%" x2="100%" y2="100%">
          <Stop offset="0%" stopColor={th.ring_border_from} />
          <Stop offset="50%" stopColor={th.dot_primary} />
          <Stop offset="100%" stopColor={th.ring_border_to} />
        </SvgLinearGradient>
        <SvgLinearGradient id={gid('wCenter')} x1="0%" y1="0%" x2="100%" y2="100%">
          <Stop offset="0%" stopColor={th.wheel_bg_from} />
          <Stop offset="100%" stopColor={th.slice_a_end} />
        </SvgLinearGradient>
        <SvgLinearGradient id={gid('sCyan')} x1="0%" y1="0%" x2="100%" y2="100%">
          <Stop offset="0%" stopColor={th.slice_a} />
          <Stop offset="100%" stopColor={th.slice_a_end} />
        </SvgLinearGradient>
        <SvgLinearGradient id={gid('sGray')} x1="0%" y1="0%" x2="100%" y2="100%">
          <Stop offset="0%" stopColor={th.slice_b} />
          <Stop offset="100%" stopColor={th.slice_b_end} />
        </SvgLinearGradient>
        <SvgLinearGradient id={gid('ptsGrad')} x1="0%" y1="0%" x2="100%" y2="0%">
          <Stop offset="0%" stopColor={th.slice_icon_star} />
          <Stop offset="100%" stopColor={th.slice_icon_points} />
        </SvgLinearGradient>
        <SvgLinearGradient id={gid('starGrad')} x1="0%" y1="0%" x2="0%" y2="100%">
          <Stop offset="0%" stopColor="#FDE68A" />
          <Stop offset="100%" stopColor={th.slice_icon_star} />
        </SvgLinearGradient>
      </Defs>

      <SvgCircle cx={cx} cy={cy} r={W_OUTER_R} fill={`url(#${gid('wBg')})`} />

      {dots.map((d, i) => (
        <SvgCircle
          key={i}
          cx={d.x}
          cy={d.y}
          r={2.25}
          fill={d.gold ? th.dot_primary : th.dot_secondary}
        />
      ))}

      <SvgCircle cx={cx} cy={cy} r={W_RING1_R} fill="none" stroke={th.ring_outer} strokeWidth={1.5} />

      <SvgCircle cx={cx} cy={cy} r={W_GOLD_R} fill="none" stroke={`url(#${gid('wGold')})`} strokeWidth={5} />

      {n > 0 && prizes.map((p, i) => {
        return (
          <Path
            key={`s${i}`}
            d={slicePath(cx, cy, W_SLICE_R, i * sdeg, (i + 1) * sdeg)}
            fill={i % 2 === 0 ? `url(#${gid('sCyan')})` : `url(#${gid('sGray')})`}
            stroke="#FFFFFF"
            strokeWidth={1.5}
          />
        );
      })}

      {n > 0 && prizes.map((p, i) => {
        const midDeg = i * sdeg + sdeg / 2;
        const midRad = (midDeg - 90) * Math.PI / 180;
        const labelR = sliceLabelRadius(n, sizeKey);
        const lx = cx + labelR * Math.cos(midRad);
        const ly = cy + labelR * Math.sin(midRad);
        const rot = `translate(${lx},${ly}) rotate(${midDeg})`;
        const custom = sliceCustomText(p);

        return (
          <G key={`l${i}`} transform={rot}>
            {custom ? (
              <WheelSliceCustomText text={custom} index={i} sliceCount={n} theme={th} prize={p} logoSize={sizeKey} />
            ) : (
              <WheelSliceDefaultIcon prize={p} index={i} sliceCount={n} theme={th} gradPrefix={gradPrefix} logoSize={sizeKey} />
            )}
          </G>
        );
      })}

      <SvgCircle cx={cx} cy={cy} r={hubR} fill={`url(#${gid('wCenter')})`} />
    </Svg>
  );
}

const spinFloatStyles = StyleSheet.create({
  column: {
    alignItems: 'flex-end',
    backgroundColor: 'transparent',
  },
  closeBtn: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  gapClose: {
    height: 6,
  },
  main: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    width: '100%',
    overflow: 'visible',
  },
  wheelSection: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
    backgroundColor: 'transparent',
    position: 'relative',
  },
  pointerWrap: {
    position: 'absolute',
    top: -1,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 4,
  },
  pointer: {
    width: 0,
    height: 0,
    borderLeftWidth: 5,
    borderRightWidth: 5,
    borderBottomWidth: 0,
    borderTopWidth: 8,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: '#FBBF24',
  },
  wheelClip: {
    width: 44,
    height: 44,
    borderRadius: 22,
    overflow: 'hidden',
    backgroundColor: '#0E7490',
    zIndex: 1,
    shadowColor: '#0B2540',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.22,
    shadowRadius: 5,
    elevation: 5,
  },
  centerLogoWrap: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    zIndex: 3,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  labelBorder: {
    height: 24,
    borderRadius: 4,
    padding: 1,
    marginTop: -3,
    alignSelf: 'stretch',
    overflow: 'hidden',
    zIndex: 6,
    elevation: 5,
  },
  labelFill: {
    flex: 1,
    borderRadius: 3,
    paddingHorizontal: 4,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 0,
  },
  labelText: {
    color: '#F8FAFC',
    fontSize: 5.5,
    lineHeight: 8,
    fontStyle: 'italic',
    fontWeight: '800',
    fontFamily: 'Poppins-Bold',
    textAlign: 'center',
  },
});

const FLOAT_WHEEL_SIZE = 44;

function SpinFloatingCluster({ prizes, onOpen, onClose, theme, logoSize, logoUrl }) {
  const th = resolveSpinWheelTheme(theme);
  const sizeKey = resolveSpinLogoSize(logoSize);
  const layout = wheelLogoLayout(sizeKey, FLOAT_WHEEL_SIZE);
  return (
    <View style={spinFloatStyles.column}>
      <TouchableOpacity
        style={spinFloatStyles.closeBtn}
        onPress={onClose}
        activeOpacity={0.8}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityLabel="Tutup promo spinning wheel"
      >
        <Ionicons name="close" size={10} color="#1E293B" />
      </TouchableOpacity>
      <View style={spinFloatStyles.gapClose} />
      <Pressable style={spinFloatStyles.main} onPress={onOpen}>
        <View style={spinFloatStyles.wheelSection}>
          <View style={spinFloatStyles.pointerWrap} pointerEvents="none">
            <View style={spinFloatStyles.pointer} />
          </View>
          <View style={spinFloatStyles.wheelClip}>
            <WheelSvg
              prizes={prizes}
              size={FLOAT_WHEEL_SIZE}
              theme={th}
              gradPrefix="swf"
              logoSize={sizeKey}
            />
          </View>
          {!!logoUrl && (
            <View
              style={[
                spinFloatStyles.centerLogoWrap,
                {
                  width: layout.wrap,
                  height: layout.wrap,
                  borderRadius: layout.wrap / 2,
                  marginLeft: -layout.wrap / 2,
                  marginTop: -layout.wrap / 2,
                  borderWidth: layout.borderW,
                  borderColor: th.hub_border,
                  backgroundColor: th.wheel_bg_from,
                },
              ]}
              pointerEvents="none"
            >
              <Image
                source={{ uri: logoUrl }}
                style={{ width: layout.logo, height: layout.logo }}
                resizeMode="contain"
              />
            </View>
          )}
        </View>
        <LinearGradient
          colors={['#FBBF24', '#F59E0B']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={spinFloatStyles.labelBorder}
        >
          <LinearGradient
            colors={['#FBBF24', '#F59E0B']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={spinFloatStyles.labelFill}
          >
            <Text style={spinFloatStyles.labelText}>
              Putar & Dapatkan{'\n'}Hadiahnya!
            </Text>
          </LinearGradient>
        </LinearGradient>
      </Pressable>
    </View>
  );
}

function PointIcon({size = 16, color = "#FFFFFF"}) {
  return (
    <Image 
      source={require('../../assets/icons/z-point-solid.svg')} 
      style={{ 
        width: size, 
        height: size, 
        tintColor: color
      }}
      resizeMode="contain"
    />
  )
}

export default function HomeScreen({ navigation }) {
  const { height: windowHeight } = useWindowDimensions();
  const spinPopupWheelSize = windowHeight < 760 ? 210 : WHEEL_SIZE;
  const spinPopupWheelCx = spinPopupWheelSize / 2;
  const spinPopupCardMaxHeight = Math.round(windowHeight * 0.92);

  const route = useRoute();
  const pendingSocialAtMount = peekPendingSocialBannerId() || route.params?.socialBannerId || null;
  const [socialBannerDeepLink, setSocialBannerDeepLink] = useState(
    pendingSocialAtMount ? { id: String(pendingSocialAtMount), nonce: Date.now() } : null
  );
  /** Share-link social banner is open: keep spinning-wheel popup hidden until the post is closed. */
  const socialBannerHoldSpinRef = React.useRef(!!pendingSocialAtMount);
  const [socialBannerHoldSpin, setSocialBannerHoldSpin] = useState(!!pendingSocialAtMount);
  const spinDeferredForSocialRef = React.useRef(false);
  /** Params spin QR terbaru (tanpa deps route.params di focus effect — hindari double fetch saat setParams clear). */
  const routeSpinParamsRef = React.useRef(route.params);
  routeSpinParamsRef.current = route.params;
  /** Setelah spin berhasil dimuat lewat QR + wajib_scan_qr, pakai ulang kode ini saat refocus (entry app mengembalikan kosong). */
  const spinStickyQrCodeRef = React.useRef(null);

  const [userData, setUserData] = useState(null);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const { showFromError: showPaygiftBlockedFromError, modal: paygiftBlockedModal } = usePaygiftClaimBlockedModal();
  const [promoData, setPromoData] = useState({ items: [], loading: true });
  const { setScrollY, setIsHomeFocused, setSpinFloatingContent } = useFloatingButton();
  const [redeemModal, setRedeemModal] = useState({ visible: false, result: null });
  /** Close without clearing result — RN Modal fade re-renders children; nulling result flashes "Redeem Gagal". */
  const closeRedeemModal = () => {
    setRedeemModal((prev) => ({ ...prev, visible: false }));
  };
  const clearRedeemModalResult = () => {
    setRedeemModal({ visible: false, result: null });
  };
  const navigateToClaimedCouponFromRedeem = (scanResult) => {
    if (!scanResult?.coupon) return;
    closeRedeemModal();
    navigateToClaimedCouponDetails(navigation, scanResult.coupon, scanResult.kawanActivation);
  };
  const [qrProcessing, setQrProcessing] = useState(false);
  const [focusOnQR, setFocusOnQR] = useState(false);
  
  const {
    confettiWrapper, 
    showConfetti
  } = useConfettiAndClap();

  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [pendingQrData, setPendingQrData] = useState(null);
  const [pendingQrValidationResult, setPendingQrValidationResult] = useState(null);
  const [profileCompletedVisible, setProfileCompletedVisible] = useState(false);
  const [polModal, setPolModal] = useState({
    visible: false,
    step: 'join',
    promotions: [],
    id_promosi: null,
    nama_promosi: null,
  });
  const [joinPolLoading, setJoinPolLoading] = useState(false);
  const [referralCheckLoading, setReferralCheckLoading] = useState(false);
  const [spinPromo, setSpinPromo] = useState({ loading: true, visible: false, data: null });
  const [showSpinFloating, setShowSpinFloating] = useState(false);
  const [spinPopupSpinning, setSpinPopupSpinning] = useState(false);
  const spinPopupAnim = useRef(new Animated.Value(0)).current;
  const spinPopupEndDeg = useRef(0);
  const spinLastLogIdRef = React.useRef(null);
  const spinClaimLockRef = React.useRef(false);
  const [spinPrizeResult, setSpinPrizeResult] = useState(null);
  const [spinResultVisible, setSpinResultVisible] = useState(false);
  const [spinClaimBusy, setSpinClaimBusy] = useState(false);
  /** Setelah klaim produk sukses: ganti blok gambar/nama dengan teks konfirmasi (tanpa Alert). */
  const [spinProductClaimSuccess, setSpinProductClaimSuccess] = useState(false);
  const [spinClaimSuccessMessage, setSpinClaimSuccessMessage] = useState('');
  /** 'shipping' | 'offline_event' | null — dari respons klaim API */
  const [spinClaimFulfillment, setSpinClaimFulfillment] = useState(null);
  const [spinRedemptionCode, setSpinRedemptionCode] = useState('');
  const [spinAddrModalVisible, setSpinAddrModalVisible] = useState(false);
  /** Konfirmasi alamat utama sebelum POST klaim produk spin */
  const [spinClaimConfirm, setSpinClaimConfirm] = useState(null);
  /** PATCH alamat utama di atas konfirmasi klaim (tanpa navigasi keluar layar spin). */
  const [spinManageAddrId, setSpinManageAddrId] = useState(null);
  /** Konteks API spin: wajib qr memakai entry_source qr + program_code */
  const [spinApiContext, setSpinApiContext] = useState({ entrySource: 'app', programCode: null });
  // Welcome modal, appear by click on ZClub logo
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  // Empty Coupon Highlight
  const [couponHighlight, setCouponHighlight] = useState({
    visible: false,
    image: null
  });
  const [incompleteModalVisible, setIncompleteModalVisible] = useState(false);
  const [hasPendingQR, setHasPendingQR] = useState(false);

  /** Setelah konten berhasil dimuat lewat entry QR — popup only, tidak pakai floating promo. */
  const spinQrPresentationActiveRef = React.useRef(false);
  const [spinQrPresentationActive, setSpinQrPresentationActive] = React.useState(false);
  const [spinPendingClaims, setSpinPendingClaims] = React.useState([]);

  const loadSpinPendingClaims = React.useCallback(async () => {
    try {
      const data = await getPendingSpinProductClaims();
      const list = Array.isArray(data?.items) ? data.items : [];
      setSpinPendingClaims(list);
    } catch (_) {
      setSpinPendingClaims([]);
    }
  }, []);

  const [igBanner, setIGBanner] = useState({data: null, loading: false});

  const { 
    bannerWrapper
  } = useBannerWithPopUp( {}, { borderRadius: 0, aspectRatio: 3.5 }, 'QRRewardResult' );

  const loadIGBanner = async () => {
    try {
      const response = await getIGBanner();
      
      if (response.success) {
        const formattedigBanner = formatIGBannerForDisplay(response.data);
        setIGBanner({data: formattedigBanner, loading: false});
      } else {
        console.log('Failed to load IG banner: ', response.error);
        setIGBanner({data: null, loading: false});
      }
    } catch (error) {
      console.log('Error loading IG banner: ', error);
      setIGBanner({data: null, loading: false});
    }
  };

  const beginSocialBannerSpinHold = React.useCallback((bannerId) => {
    socialBannerHoldSpinRef.current = true;
    setSocialBannerHoldSpin(true);
    setShowSpinFloating(false);
    if (bannerId) {
      setSocialBannerDeepLink((prev) => prev || { id: String(bannerId), nonce: Date.now() });
    }
  }, []);

  /**
   * @param {{ entrySource?: string; programCode?: string|null; forcePopup?: boolean; preserveMainVisible?: boolean }} [opts]
   */
  const loadSpinPromo = React.useCallback(async (opts) => {
    const entrySource = opts?.entrySource ?? 'app';
    const programCodeParam = opts?.programCode ?? null;
    const forcePopup = opts?.forcePopup ?? false;
    const viaQrChannel = entrySource === 'qr';

    try {
      const pendingSocialId = await hydratePendingSocialBannerId();
      if (pendingSocialId || socialBannerHoldSpinRef.current || routeSpinParamsRef.current?.socialBannerId) {
        beginSocialBannerSpinHold(pendingSocialId || routeSpinParamsRef.current?.socialBannerId);
      }

      const data = await getActiveSpinningWheel('club', entrySource, programCodeParam);
      const program = data?.program || null;
      const prizes = data?.prizes || [];
      const hasStockBackedPrize = prizes.some((p) => String(p?.prize_type || 'none') !== 'none');

      if (!program || prizes.length === 0 || !hasStockBackedPrize) {
        spinStickyQrCodeRef.current = null;
        spinQrPresentationActiveRef.current = false;
        setSpinQrPresentationActive(false);

        /* QR tidak valid / nonaktif / luar tanggal: jangan hapus UI discovery & floating yang sudah ada */
        if (viaQrChannel) {
          if (forcePopup) {
            Alert.alert(
              'Spinning Wheel',
              'Program dari QR tidak ditemukan atau belum bisa diputar. Pastikan QR sesuai program yang masih aktif.'
            );
          }
          try {
            await loadSpinPromo();
          } catch {
            setSpinPromo({ loading: false, visible: false, data: null });
            setShowSpinFloating(false);
            setSpinApiContext({ entrySource: 'app', programCode: null });
          }
          return;
        }

        setSpinPromo({ loading: false, visible: false, data: null });
        setShowSpinFloating(false);
        setSpinApiContext({ entrySource: 'app', programCode: null });
        return;
      }

      const resolvedCode =
        programCodeParam != null && programCodeParam !== ''
          ? programCodeParam
          : program.program_qr_code || null;
      setSpinApiContext({ entrySource, programCode: resolvedCode });

      const wajibQr = !!(program.wajib_scan_qr === true || program.wajib_scan_qr === 1 || program.wajib_scan_qr === '1');
      if (wajibQr && entrySource === 'qr' && resolvedCode) {
        spinStickyQrCodeRef.current = resolvedCode;
      } else if (!wajibQr) {
        spinStickyQrCodeRef.current = null;
      }

      spinQrPresentationActiveRef.current = viaQrChannel;
      setSpinQrPresentationActive(viaQrChannel);

      const holdForSocialBanner =
        socialBannerHoldSpinRef.current || !!routeSpinParamsRef.current?.socialBannerId;

      if (opts?.preserveMainVisible) {
        const dismissKey = `spin_popup_closed:${program.id || program.program_qr_code || 'default'}`;
        const dismissedOn = await AsyncStorage.getItem(dismissKey);
        const today = new Date().toISOString().slice(0, 10);
        const shouldShowPopup = dismissedOn !== today;

        setSpinPromo((prev) => ({
          loading: false,
          visible: !!prev.visible,
          data: {
            ...program,
            prizes,
          },
        }));
        setShowSpinFloating(!viaQrChannel && !shouldShowPopup);
        return;
      }

      if (forcePopup) {
        if (holdForSocialBanner) {
          spinDeferredForSocialRef.current = true;
          setSpinPromo({
            loading: false,
            visible: false,
            data: {
              ...program,
              prizes,
            },
          });
          setShowSpinFloating(false);
          return;
        }
        setSpinPromo({
          loading: false,
          visible: true,
          data: {
            ...program,
            prizes,
          },
        });
        setShowSpinFloating(false);
        return;
      }

      const dismissKey = `spin_popup_closed:${program.id || program.program_qr_code || 'default'}`;
      const dismissedOn = await AsyncStorage.getItem(dismissKey);
      const today = new Date().toISOString().slice(0, 10);
      const shouldShowPopup = dismissedOn !== today;

      if (shouldShowPopup && holdForSocialBanner) {
        spinDeferredForSocialRef.current = true;
        setSpinPromo({
          loading: false,
          visible: false,
          data: {
            ...program,
            prizes,
          },
        });
        setShowSpinFloating(false);
        return;
      }

      if (!shouldShowPopup && holdForSocialBanner) {
        setSpinPromo({
          loading: false,
          visible: false,
          data: {
            ...program,
            prizes,
          },
        });
        setShowSpinFloating(false);
        return;
      }

      setSpinPromo({
        loading: false,
        visible: shouldShowPopup,
        data: {
          ...program,
          prizes,
        },
      });
      /* Tanpa QR: popup otomatis pertama / floating setelah tutup. Via QR: tidak pernah floating. */
      setShowSpinFloating(!viaQrChannel && !shouldShowPopup);
    } catch (e) {
      spinStickyQrCodeRef.current = null;

      if (viaQrChannel) {
        spinQrPresentationActiveRef.current = false;
        setSpinQrPresentationActive(false);
        if (opts?.forcePopup) {
          Alert.alert('Spinning Wheel', e?.message || 'Gagal memuat program');
        }
        try {
          await loadSpinPromo();
        } catch {
          setSpinPromo({ loading: false, visible: false, data: null });
          setShowSpinFloating(false);
          setSpinApiContext({ entrySource: 'app', programCode: null });
        }
        return;
      }

      spinQrPresentationActiveRef.current = false;
      setSpinQrPresentationActive(false);
      setSpinPromo({ loading: false, visible: false, data: null });
      setShowSpinFloating(false);
      setSpinApiContext({ entrySource: 'app', programCode: null });
      if (opts?.forcePopup) {
        Alert.alert('Spinning Wheel', e?.message || 'Gagal memuat program');
      }
    }
  }, [beginSocialBannerSpinHold]);

  const closeSpinResultAndRefresh = React.useCallback(() => {
    setSpinManageAddrId(null);
    setSpinClaimConfirm(null);
    setSpinProductClaimSuccess(false);
    setSpinClaimSuccessMessage('');
    setSpinClaimFulfillment(null);
    setSpinRedemptionCode('');
    setSpinResultVisible(false);
    loadSpinPromo({
      entrySource: spinApiContext.entrySource || 'app',
      programCode: spinApiContext.programCode ?? null,
      preserveMainVisible: true,
    });
  }, [loadSpinPromo, spinApiContext.entrySource, spinApiContext.programCode, loadSpinPendingClaims]);

  const runSpinWheelProductClaim = React.useCallback(async (logId, idAlamatPassed) => {
    spinClaimLockRef.current = true;
    setSpinClaimBusy(true);
    try {
      const idUse = idAlamatPassed != null && idAlamatPassed > 0 ? idAlamatPassed : undefined;
      const claimData = await postSpinWheelClaimProduct(logId, idUse);
      spinLastLogIdRef.current = null;
      const fulfillment = claimData?.fulfillment === 'offline_event' ? 'offline_event' : 'shipping';
      const redemption = claimData?.redemption_code ? String(claimData.redemption_code) : '';
      setSpinClaimFulfillment(fulfillment);
      setSpinRedemptionCode(redemption);
      const already = !!claimData?.already_claimed;
      const noTrx = claimData?.no_transaksi ? String(claimData.no_transaksi) : '';
      let main = '';
      if (fulfillment === 'offline_event') {
        main = already
          ? 'Hadiah booth ini sudah pernah diklaim.'
          : 'Klaim berhasil. Tunjukkan kode di bawah ke panitia.';
      } else {
        main = already
          ? 'Hadiah ini sudah pernah diklaim. Pesanan tetap bisa dicek di daftar pesanan kamu.'
          : 'Hadiah berhasil diklaim dan diproses ke alamat utama kamu.';
        const extra = noTrx ? `\nNomor pesanan: ${noTrx}` : '';
        main += extra;
      }
      setSpinClaimSuccessMessage(main);
      setSpinProductClaimSuccess(true);
      void loadSpinPendingClaims();
    } catch (err) {
      const msg = err?.message || 'Klaim gagal';
      const needAddr = /alamat/i.test(msg) || /pengiriman/i.test(msg) || /lengkapi/i.test(msg);
      if (needAddr) {
        Alert.alert('Perhatian', msg, [
          { text: 'Nanti', style: 'cancel' },
          { text: 'Isi alamat', onPress: () => setSpinAddrModalVisible(true) },
        ]);
      } else {
        Alert.alert('Spinning Wheel', msg);
      }
    } finally {
      spinClaimLockRef.current = false;
      setSpinClaimBusy(false);
    }
  }, [loadSpinPendingClaims]);

  const claimSpinWheelProductClub = React.useCallback(async () => {
    if (spinClaimLockRef.current) return;
    const logId = spinLastLogIdRef.current;
    if (!logId) {
      Alert.alert('Spinning Wheel', 'Data klaim tidak ditemukan. Silakan putar lagi.');
      return;
    }

    const boothOffline = spinPromo?.data?.product_fulfillment === 'offline_event';
    if (boothOffline) {
      void runSpinWheelProductClaim(logId, null);
      return;
    }

    spinClaimLockRef.current = true;
    setSpinClaimBusy(true);
    try {
      const list = await getShippingAddresses();
      const primary = list.find((a) => Number(a.alamat_utama) === 1);
      if (!primary) {
        spinClaimLockRef.current = false;
        setSpinClaimBusy(false);
        setSpinAddrModalVisible(true);
        return;
      }
      spinClaimLockRef.current = false;
      setSpinClaimBusy(false);
      setSpinClaimConfirm({ addr: primary });
      return;
    } catch (e) {
      const msg = e?.message || 'Gagal memeriksa alamat';
      Alert.alert('Spinning Wheel', msg);
      spinClaimLockRef.current = false;
      setSpinClaimBusy(false);
    }
  }, [runSpinWheelProductClaim, spinPromo?.data?.product_fulfillment]);

  const onSpinShippingAddressSaved = React.useCallback(
    (addr) => {
      setSpinAddrModalVisible(false);
      const lid = spinLastLogIdRef.current;
      const aid = addr && typeof addr.id === 'number' ? addr.id : 0;
      if (lid && aid > 0) {
        void runSpinWheelProductClaim(lid, aid);
      }
    },
    [runSpinWheelProductClaim],
  );

  const [fontsLoaded] = useFonts({
    'Poppins': require('../../assets/fonts/Poppins-Regular.ttf'),
    'Poppins-Medium': require('../../assets/fonts/Poppins-Medium.ttf'),
    'Poppins-SemiBold': require('../../assets/fonts/Poppins-SemiBold.ttf'),
    'Poppins-Bold': require('../../assets/fonts/Poppins-Bold.ttf'),
  });

  const dummyImage = 'https://dummyimage.com/390x145/e2e8f0/000000&text=+';
  const portraitDummyImage = 'https://dummyimage.com/450x600/e2e8f0/000000&text=+';

  const releaseSocialBannerSpinHold = React.useCallback(() => {
    socialBannerHoldSpinRef.current = false;
    setSocialBannerHoldSpin(false);
    if (!spinDeferredForSocialRef.current) return;
    spinDeferredForSocialRef.current = false;
    setSpinPromo((prev) => {
      if (!prev.data) return prev;
      return { ...prev, visible: true };
    });
    setShowSpinFloating(false);
  }, []);

  /*
   * Deep link / scan QR often lands on Home that is already focused.
   * useFocusEffect will not re-run in that case, so open the wheel from route params here.
   */
  React.useEffect(() => {
    const p = route.params || {};
    if (!p.socialBannerId) return;
    beginSocialBannerSpinHold(p.socialBannerId);
    setSocialBannerDeepLink({
      id: String(p.socialBannerId),
      nonce: p.socialBannerNonce || Date.now(),
    });
    setSpinPromo((prev) => {
      if (!prev.visible) return prev;
      spinDeferredForSocialRef.current = true;
      return { ...prev, visible: false };
    });
    setShowSpinFloating(false);
    navigation.setParams({
      socialBannerId: undefined,
      socialBannerNonce: undefined,
    });
  }, [navigation, beginSocialBannerSpinHold, route.params?.socialBannerId, route.params?.socialBannerNonce]);

  React.useEffect(() => {
    const p = route.params || {};
    if (!p.openSpinFromQr || !p.spinProgramCode) {
      return;
    }
    const code = String(p.spinProgramCode);
    navigation.setParams({
      openSpinFromQr: undefined,
      spinProgramCode: undefined,
      spinQrScanNonce: undefined,
    });
    loadSpinPromo({ entrySource: 'qr', programCode: code, forcePopup: true });
  }, [
    navigation,
    loadSpinPromo,
    route.params?.openSpinFromQr,
    route.params?.spinProgramCode,
    route.params?.spinQrScanNonce,
  ]);

  const loadUnreadNotifCount = React.useCallback(async () => {
    try {
      const count = await notificationApi.getUnreadCount();
      setUnreadNotifCount(count);
    } catch (_e) {
      setUnreadNotifCount(0);
    }
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      loadUserData();
      loadPromoData();
      void loadSpinPendingClaims();
      void loadUnreadNotifCount();

      /*
       * Dua jalur Club (backend mendukung keduanya):
       * - Tanpa QR: GET active entry_source=app — untuk program tanpa wajib_scan_qr (atau discover umum).
       * - QR: params openSpinFromQr / sticky spinStickyQrCodeRef — entry_source=qr + program_code.
       *   Popup langsung (forcePopup), tidak memakai floating promo (spinQrPresentationActive).
       */
      const p = routeSpinParamsRef.current || {};
      if (p.openSpinFromQr && p.spinProgramCode) {
        /* Params effect membuka popup; jangan fetch jalur app di sini. */
      } else if (spinStickyQrCodeRef.current) {
        loadSpinPromo({
          entrySource: 'qr',
          programCode: spinStickyQrCodeRef.current,
          forcePopup: false,
        });
      } else {
        loadSpinPromo();
      }

      // tell the floating button we're on home so it moves to the top position
      setIsHomeFocused(true);
      setScrollY(0);
      return () => {
        // leaving home — drop the button back to the bottom
        // setIsHomeFocused(false);
        setSpinFloatingContent(null);
      };
    }, [navigation, loadSpinPromo, loadSpinPendingClaims, loadUnreadNotifCount])
  );

  const spinPopupRotate = spinPopupAnim.interpolate({
    inputRange: [0, 500000],
    outputRange: ['0deg', '500000deg'],
    extrapolateRight: 'extend',
    extrapolateLeft: 'clamp',
  });

  // Derived: apakah kuota spin user sudah habis untuk program ini
  const spinRemainingCount = React.useMemo(() => {
    const d = spinPromo?.data;
    if (!d) return 0;
    const max = Number(d.max_spin_per_user ?? 0);
    return typeof d.spins_remaining === 'number'
      ? Math.max(0, d.spins_remaining)
      : Math.max(0, max - Number(d.spins_used ?? 0));
  }, [spinPromo]);

  const spinIsExhausted = React.useMemo(() => {
    const d = spinPromo?.data;
    if (!d) return false;
    const max = Number(d.max_spin_per_user ?? 0);
    if (max <= 0) return false;
    return spinRemainingCount <= 0;
  }, [spinPromo, spinRemainingCount]);

  const spinUsesDailyQuota = React.useMemo(() => {
    const d = spinPromo?.data;
    if (!d) return false;
    return d.web_daily_spin === true || d.web_daily_spin === 1 || d.web_daily_spin === '1';
  }, [spinPromo]);

  const spinBadgeLabel = React.useMemo(() => {
    if (spinIsExhausted) {
      return spinUsesDailyQuota
        ? 'Kesempatan sudah habis, tunggu besok lagi ya!'
        : 'Kesempatan sudah habis';
    }
    const base = `${spinRemainingCount}x kesempatan`;
    return spinUsesDailyQuota ? `${base} perhari` : base;
  }, [spinIsExhausted, spinUsesDailyQuota, spinRemainingCount]);

  const openSpinProgram = async () => {
    const promo = spinPromo?.data;
    const prizes = promo?.prizes || [];
    if (!promo || prizes.length === 0 || spinPopupSpinning) return;

    const maxSpin = Number(promo.max_spin_per_user ?? 0);
    const used = Number(promo.spins_used ?? 0);
    const remaining =
      typeof promo.spins_remaining === 'number' && !Number.isNaN(promo.spins_remaining)
        ? promo.spins_remaining
        : Math.max(0, maxSpin - used);

    if (maxSpin > 0 && remaining <= 0) {
      return; // UI sudah menampilkan sold-out state, tidak perlu Alert
    }

    setSpinPopupSpinning(true);
    spinLastLogIdRef.current = null;
    const start = spinPopupEndDeg.current;
    spinPopupAnim.setValue(start);

    // Start moving immediately so the button never feels unresponsive while the API runs.
    Animated.timing(spinPopupAnim, {
      toValue: start + 1080,
      duration: 1600,
      easing: Easing.linear,
      useNativeDriver: false,
    }).start();

    try {
      const data = await postSpinningWheel(
        'club',
        spinApiContext.entrySource || 'app',
        spinApiContext.programCode ?? promo.program_qr_code ?? null
      );
      const snapshot =
        Array.isArray(data.wheel_prizes) && data.wheel_prizes.length > 0 ? data.wheel_prizes : prizes;
      const n = snapshot.length;
      if (n < 1) {
        throw new Error('Data roda tidak valid');
      }
      const slice = 360 / n;
      const idx = parsePrizeIndexSpin(data.prize_index, n - 1);
      const centerDeg = idx * slice + slice / 2;
      const fullSpins = (5 + Math.floor(Math.random() * 2)) * 360;
      const currentValue = await new Promise((resolve) => {
        spinPopupAnim.stopAnimation((value) => resolve(Number(value) || start));
      });
      const remainder = mod360Spin(currentValue);
      const extra = fullSpins + mod360Spin(360 - remainder + (360 - centerDeg));
      const end = currentValue + extra;

      setSpinPromo((prev) => ({
        ...prev,
        data: {
          ...(prev?.data || {}),
          ...(data.program || {}),
          prizes: snapshot,
        },
      }));

      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });

      await new Promise((resolve) => {
        Animated.timing(spinPopupAnim, {
          toValue: end,
          duration: 3200,
          easing: Easing.bezier(0.14, 0.92, 0.24, 1),
          useNativeDriver: false,
        }).start(() => {
          spinPopupEndDeg.current = end;
          resolve();
        });
      });

      const sid = data?.spin_log_id;
      const sidNum = typeof sid === 'number' ? sid : parseInt(String(sid ?? ''), 10);
      spinLastLogIdRef.current = !Number.isNaN(sidNum) && sidNum > 0 ? sidNum : null;

      setSpinProductClaimSuccess(false);
      setSpinClaimSuccessMessage('');
      setSpinClaimFulfillment(null);
      setSpinRedemptionCode('');
      const prize = data?.prize ?? null;
      setSpinPrizeResult(prize);
      if (prize && prize.prize_type === 'points') {
        const after = prize.total_points_after != null ? Number(prize.total_points_after) : NaN;
        const credited = Number(prize.points_credited ?? prize.points_value ?? 0);
        setUserData((prev) => {
          if (!prev) return prev;
          const nextPoints = !Number.isNaN(after)
            ? after
            : Number(prev.points || 0) + (Number.isNaN(credited) ? 0 : credited);
          return { ...prev, points: nextPoints };
        });
        try {
          const raw = await AsyncStorage.getItem('userData');
          if (raw) {
            const cached = JSON.parse(raw);
            cached.points = !Number.isNaN(after)
              ? after
              : Number(cached.points || 0) + (Number.isNaN(credited) ? 0 : credited);
            await AsyncStorage.setItem('userData', JSON.stringify(cached));
          }
        } catch (cacheErr) {
          console.log('Failed to persist spun points:', cacheErr?.message);
        }
      }
      setSpinResultVisible(true);
    } catch (e) {
      spinLastLogIdRef.current = null;
      spinPopupAnim.stopAnimation((value) => {
        spinPopupEndDeg.current = Number(value) || start;
      });
      Alert.alert('Spinning Wheel', e?.message || 'Spin gagal');
    } finally {
      setSpinPopupSpinning(false);
    }
  };

  const closeSpinFloating = React.useCallback(async () => {
    setShowSpinFloating(false);
  }, []);

  const closeSpinPopup = async () => {
    const code = spinPromo?.data?.id || spinPromo?.data?.program_qr_code || 'default';
    const dismissKey = `spin_popup_closed:${code}`;
    const today = new Date().toISOString().slice(0, 10);
    await AsyncStorage.setItem(dismissKey, today);

    const wasQrPresentation = spinQrPresentationActiveRef.current;

    setSpinPromo((prev) => ({ ...prev, visible: false }));

    /*
     * Program wajib_scan_qr menyimpan spinStickyQrCodeRef agar refocus tetap pakai entry qr.
     * Setelah user menutup popup sesi QR, jangan biarkan itu mengalahkan program discovery (app):
     * reload entry app supaya floating spinning wheel umum bisa muncul lagi.
     */
    if (wasQrPresentation) {
      spinStickyQrCodeRef.current = null;
      spinQrPresentationActiveRef.current = false;
      setSpinQrPresentationActive(false);
      await loadSpinPromo();
      return;
    }

    setShowSpinFloating(!spinQrPresentationActiveRef.current);
  };

  useEffect(() => {
    const show =
      showSpinFloating &&
      spinPromo?.data &&
      !spinPromo.visible &&
      !spinQrPresentationActive &&
      !socialBannerHoldSpin;
    if (!show) {
      setSpinFloatingContent(null);
      return;
    }
    const prizes = spinPromo?.data?.prizes || [];
    setSpinFloatingContent(
      <SpinFloatingCluster
        prizes={prizes}
        theme={spinPromo?.data?.ui_theme}
        logoSize={spinPromo?.data?.logo_size}
        logoUrl={spinPromo?.data?.logo_url}
        onOpen={() => setSpinPromo((prev) => ({ ...prev, visible: true }))}
        onClose={() => void closeSpinFloating()}
      />
    );
  }, [
    showSpinFloating,
    spinPromo.visible,
    spinPromo?.data,
    spinQrPresentationActive,
    socialBannerHoldSpin,
    closeSpinFloating,
    setSpinFloatingContent,
  ]);

  // Set document title for web platform
  useEffect(() => {
    if (Platform.OS === 'web') {
      document.title = 'Zoolyfe Club';
    }
  }, []);

  const loadUserData = async () => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');
      
      if (!userToken || !sessionId) {
        doLogout();
        return;
      }
      
      // Try to fetch fresh data from backend first
      try {
        const response = await fetchUserProfile();
        if (response.data) {
          const apiUserData = response.data;
          // Map API response fields to expected structure
          setUserData({
            ...apiUserData,
            name: apiUserData.user_fullname || apiUserData.name || 'User',
            points: apiUserData.points || 0,
            stamps: apiUserData.stamps || 0,
          });

          return; // Exit early if backend fetch was successful
        }
      } catch (backendError) {
        console.log('Backend fetch failed, using cached data:', backendError.message);
      }
      
      // Fallback to cached data if backend fetch fails
      const userDataString = await AsyncStorage.getItem('userData');
      if (userDataString) {
        const apiUserData = JSON.parse(userDataString);
        // Map API response fields to expected structure
        setUserData({
          ...apiUserData,
          name: apiUserData.user_fullname || apiUserData.name || 'User',
          points: apiUserData.points || 0,
          stamps: apiUserData.stamps || 0,
        });
      }
    } catch (error) {
      console.log('Error loading user data:', error);
      doLogout();
    }
  };

  const loadPromoData = async () => {
    try {
      setPromoData(prev => ({ ...prev, loading: true }));
      const response = await getPromoCornerData();
      
      // console.log('Promo Corner API Response:', JSON.stringify(response, null, 2));
      // console.log('Promo items with images:', response.data.items?.map(item => ({ title: item.title, image: item.image, hasImage: !!item.image })));
      
      // Debug: Show alert with image info
      if (response.data.items && response.data.items.length > 0) {
        const imageInfo = response.data.items.map(item => `${item.title}: ${item.image || 'NO IMAGE'}`).join('\n');
        // console.log('DEBUG - Image URLs:', imageInfo);
      }
      
      if (response.success) {
        // console.log('Promo items found:', response.data.items?.length || 0);
        // console.log('E-stamps:', response.data.estamps?.length || 0);
        // console.log('Vouchers:', response.data.vouchers?.length || 0);
        
        setPromoData({
          items: response.data.items || [],
          loading: false
        });
      } else {
        console.log('Failed to load promo data:', response.error);
        setPromoData({ items: [], loading: false });
      }
    } catch (error) {
      console.log('Error loading promo data:', error);
      setPromoData({ items: [], loading: false });
    }
  };

  // Timeout promise for API calls (avoid endless loading on slow/hang)
  const withTimeout = (promise, ms, label) => {
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label || 'Request'} timeout (${ms / 1000}s)`)), ms)
    );
    return Promise.race([promise, timeout]);
  };

  // Function to try redeeming any pending QR code after login
  const tryRedeemPendingQr = async () => {
    const isUserSetRemind = await AsyncStorage.getItem('remindLater');
    const isAutoClaimPendingQR = await AsyncStorage.getItem('autoClaimPendingQR');
    const pendingQr = await getLatestPendingQR();
    // Process the QR code - clean non-numeric characters first
    const cleanedQrData = (pendingQr ?? '').replace(/[^0-9]/g, '');

    try {
      if (pendingQr && userData) {
        setFocusOnQR(true);
        setQrProcessing(true);

        if(userData.user_profile_completed_at) {
          // check if there is qr that must be claimed after complete profile detail
          // if yes, dont remove the QR yet, because user still needs to click on claim button
          if( !isAutoClaimPendingQR ) {
            await resetPendingQRs();
          }
        } else {
          if(!!isUserSetRemind) {
            setHasPendingQR(true);
            setIncompleteModalVisible(true);
            return;
          }
        }
        
        // First validate the QR code to show information (with timeout to avoid endless loading)
        const VALIDATE_TIMEOUT_MS = 20000;
        try {
          const validateResponse = await withTimeout(
            validateQrCode(cleanedQrData),
            VALIDATE_TIMEOUT_MS,
            'Validasi QR'
          );
          const validationResult = transformValidateResult(validateResponse);
          // Store the QR data for later claiming and show validation modal
          setPendingQrData(cleanedQrData);
          setPendingQrValidationResult(validationResult);
          
        } catch (validateError) {
          console.log('QR validation failed:', validateError.message);
          // If timeout, show error and skip fallback to avoid another long wait
          if (validateError.message && validateError.message.includes('timeout')) {
            setRedeemModal({
              visible: true,
              result: {
                type: 'network',
                title: 'Validasi terlalu lama',
                message: 'Koneksi lambat. Silakan coba lagi.',
                icon: 'time',
                color: '#F44336',
              },
            });
            return;
          }

          // incomplete user must be skipped from trying direct scan
          // they must complete their profile first at any cost!
          if(!userData.user_profile_completed_at) {
            throw validateError;
            return;
          }

          // If validation fails, try direct scan as fallback (with timeout)
          const scanResponse = await withTimeout(
            scanQrCode(cleanedQrData),
            VALIDATE_TIMEOUT_MS,
            'Scan QR'
          );
          const result = transformScanResult(scanResponse);
          if (!result) {
            throw new Error('Invalid API response');
          }
          
          // Clear the pending QR code
          if(isAutoClaimPendingQR || !userData.user_profile_completed_at) {
            await deletePendingQR(cleanedQrData);
          } else {
            await resetPendingQRs();
          }
          
          // Fetch latest user data after successful QR scan
          try {
            console.log('Fetching latest user data after successful QR scan');
            const userResponse = await fetchUserProfile();
            if (userResponse.data) {
              const apiUserData = userResponse.data;
              // Update application state with fresh user data
              setUserData({
                ...apiUserData,
                name: apiUserData.user_fullname || apiUserData.name || 'User',
                points: apiUserData.points || 0,
                stamps: apiUserData.stamps || 0,
              });
              console.log('User data successfully updated after QR scan');
            }
          } catch (refreshError) {
            console.warn('Failed to refresh user data after QR scan:', refreshError.message);
            // Continue with the redeem modal even if refresh fails
          }
          
          // Redeem modal only on direct-scan fallback (validation failed)
          setRedeemModal({ visible: true, result });
        }
      } else {
        if( userData && !userData.user_profile_completed_at ) {
          setFocusOnQR(true);
          setHasPendingQR(false);
          setIncompleteModalVisible(true);
          // doLogout();
        }
      }
    } catch (error) {
      console.log('Failed to redeem pending QR code:', error);

      if (showPaygiftBlockedFromError(error)) {
        return;
      }

      // Clear the pending QR code
      if(isAutoClaimPendingQR || !userData.user_profile_completed_at) {
        await deletePendingQR(cleanedQrData);
      } else {
        await resetPendingQRs();
      }
      
      // Create error result object similar to ScanScreen.js
      let errorResult;
      
      if (error.message.includes('already scanned') || error.message.includes('already used')) {
        errorResult = {
          type: 'already_used',
          title: 'QR Code Already Used',
          message: 'This QR code has already been scanned and cannot be used again',
          icon: 'close-circle',
          color: '#F44336',
        };
      } else if (error.message.includes('expired')) {
        let expiredDate = null;

        if (error.data?.tanggal_berakhir && !isNaN(error.data?.tanggal_berakhir)) {
          expiredDate = new Date( Number( error.data.tanggal_berakhir ) * 1000 );
        }

        errorResult = {
          type: 'expired',
          title: 'QR Code Expired',
          // message: 'This QR code has expired and is no longer valid',
          message: 'Periode klaim voucher code telah berakhir',
          icon: 'time',
          color: '#F44336',
          errorData: {
            expiredDate: expiredDate
          }
        };
      } else if (error.message.includes('not found') || error.message.includes('404')) {
        errorResult = {
          type: 'invalid',
          title: 'Invalid QR Code',
          message: 'This QR code is not recognized by Zoolyfe Club',
          icon: 'close-circle',
          color: '#F44336',
        };
      } else if (error.message && error.message.includes('timeout')) {
        errorResult = {
          type: 'network',
          title: 'Terlalu lama',
          message: 'Koneksi lambat atau server sibuk. Silakan coba lagi.',
          icon: 'time',
          color: '#F44336',
        };
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorResult = {
          type: 'network',
          title: 'Connection Error',
          message: 'Please check your internet connection and try again',
          icon: 'wifi-off',
          color: '#F44336',
        };
      } else if (error.message.includes('Session ID is required')) {
        errorResult = {
          type: 'session_expired',
          title: 'Sesi telah berakhir.',
          message: 'Silakan login kembali untuk melanjutkan.',
          icon: 'lock-closed',
          color: '#F44336',
        };
      } else {
        errorResult = {
          type: 'error',
          title: 'Scan Failed',
          message: error.message || 'Failed to process QR code. Please try again.',
          icon: 'alert-circle',
          color: '#F44336',
        };
      }

      // Show error modal but keep the QR code for manual retry
      setRedeemModal({ visible: true, result: errorResult });
    } finally {
      // Reset loading state regardless of success or failure
      setQrProcessing(false);
    }
  };

  // Function to handle accepting the reward after validation
  const handleAcceptReward = async () => {
    if (!pendingQrData) {
      setFocusOnQR(false);
      return;
    }
    
    setProfileCompletedVisible(false);
    setValidationModalVisible(false);
    setQrProcessing(true);
    
    try {
      // Process the actual QR code scan to claim rewards
      const scanResponse = await scanQrCode(pendingQrData);
      const result = transformScanResult(scanResponse);
      if (!result) {
        throw new Error('Invalid API response');
      }
      
      // Fetch latest user data after successful QR scan
      try {
        console.log('Fetching latest user data after successful QR scan');
        const userResponse = await fetchUserProfile();
        if (userResponse.data) {
          const apiUserData = userResponse.data;
          // Update application state with fresh user data
          setUserData({
            ...apiUserData,
            name: apiUserData.user_fullname || apiUserData.name || 'User',
            points: apiUserData.points || 0,
            stamps: apiUserData.stamps || 0,
          });
          console.log('User data successfully updated after QR scan');
        }
      } catch (refreshError) {
        console.warn('Failed to refresh user data after QR scan:', refreshError.message);
        // Continue with the redeem modal even if refresh fails
      }
      
      // Show success modal
      if (shouldDirectToCouponAfterKawanClaim(result)) {
        navigateToClaimedCouponFromRedeem(result);
        return;
      }

      setRedeemModal({ visible: true, result });
      // POL popup only when QR is from a Zoolyfe Club batch (admin batch link).
      const isSuccessType = result && !['invalid', 'already_used', 'already_scanned', 'expired', 'network', 'error', 'session_expired'].includes(result.type);
      const refOptsAfterScan = referralOptionsAfterScan(result);
      if (isSuccessType && !refOptsAfterScan) {
        setPolModal({ visible: false, step: 'join', promotions: [], id_promosi: null, nama_promosi: null });
      }
      if (isSuccessType && refOptsAfterScan) {
        getReferralAvailable(refOptsAfterScan).then((r) => {
          const promos = normalizeReferralPromotions(r);
          if (promos.length === 1) {
            setPolModal({
              visible: false,
              step: 'join',
              promotions: [],
              id_promosi: promos[0].id_promosi,
              nama_promosi: promos[0].nama_promosi || '',
            });
          } else if (promos.length > 1) {
            setPolModal({
              visible: false,
              step: 'pick',
              promotions: promos,
              id_promosi: null,
              nama_promosi: null,
            });
          }
        }).catch(() => {});
      }
      
    } catch (error) {
      console.error('Failed to accept reward:', error.message);

      if (showPaygiftBlockedFromError(error)) {
        return;
      }
      
      // Handle different error types
      let errorResult;
      if (error.message.includes('already used') || error.message.includes('already scanned')) {
        errorResult = {
          type: 'already_used',
          title: 'QR Code Already Used',
          message: 'This QR code has already been scanned and cannot be used again',
          icon: 'close-circle',
          color: '#F44336',
        };
      } else if (error.message.includes('expired')) {
        let expiredDate = null;

        if (error.data?.tanggal_berakhir && !isNaN(error.data?.tanggal_berakhir)) {
          expiredDate = new Date( Number( error.data.tanggal_berakhir ) * 1000 );
        }

        errorResult = {
          type: 'expired',
          title: 'QR Code Expired',
          // message: 'This QR code has expired and is no longer valid',
          message: 'Periode klaim voucher code telah berakhir',
          icon: 'time',
          color: '#F44336',
          errorData: {
            expiredDate: expiredDate
          }
        };
      } else if (error.message.includes('not found') || error.message.includes('404')) {
        errorResult = {
          type: 'invalid',
          title: 'Invalid QR Code',
          message: 'This QR code is not recognized by Zoolyfe Club',
          icon: 'close-circle',
          color: '#F44336',
        };
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorResult = {
          type: 'network',
          title: 'Connection Error',
          message: 'Please check your internet connection and try again',
          icon: 'wifi-off',
          color: '#F44336',
        };
      } else if (error.message.includes('Session ID is required')) {
        errorResult = {
          type: 'session_expired',
          title: 'Sesi telah berakhir.',
          message: 'Silakan login kembali untuk melanjutkan.',
          icon: 'lock-closed',
          color: '#F44336',
        };
      } else {
        errorResult = {
          type: 'error',
          title: 'Scan Failed',
          message: error.message || 'Failed to process QR code. Please try again.',
          icon: 'alert-circle',
          color: '#F44336',
        };
      }

      // Show error modal
      setRedeemModal({ visible: true, result: errorResult });
    } finally {
      setQrProcessing(false);
    }
  };

  // Check for pending QR code redemption when component mounts and when screen is focused
  useEffect(() => {
    async function redeemOrVerify() {

      if (userData && userData.name) {
        // if user is not verified yet, redirect them to verification screen
        if( userData.user_profile_completed_at && !userData.user_verified_at ) {
          navigation.replace('Verification');
          return;
        } else {
          // if user is logged in from external source and not set phone yet, force them to input phone
          if( !userData.user_profile_completed_at && !!userData.user_email && !userData.user_telp ) {
            navigation.replace('PhoneForm');
            return;
          }

          // check if user completed their profile somewhere else recently, after last QR added
          const lastQRTimestamp = await AsyncStorage.getItem('last_pending_qr_timestamp');
          console.log(`Comparing user's profile_completed_at VS last_pending_qr_timestamp`);
          // console.log(`profile_completed_at : ${Number( userData.user_profile_completed_at ) * 1000}`);
          // console.log(`last_pending_qr_timestamp : ${lastQRTimestamp}`);

          // console.log([ !!userData.user_profile_completed_at 
          //   , !!lastQRTimestamp
          //   , !isNaN( userData.user_profile_completed_at )
          //   , !isNaN( lastQRTimestamp ) 
          //   , Number( userData.user_profile_completed_at ) * 1000 > Number( lastQRTimestamp ?? 0 ) ]);

          if ( 
            !!userData.user_has_special_login 
            && !!userData.user_profile_completed_at 
            && !!lastQRTimestamp
            && !isNaN( userData.user_profile_completed_at )
            && !isNaN( lastQRTimestamp ) 
            && Number( userData.user_profile_completed_at ) * 1000 > Number( lastQRTimestamp ?? 0 )
          ) {
            // activate auto claim mechanism
            await AsyncStorage.setItem('autoClaimPendingQR', 'autoClaimPendingQR');
          }

          // if in the middle of QR processing userdata somehow changed
          // prevent another round of QR processing being started
          if(!pendingQrData) {
            tryRedeemPendingQr();
          }
        }
      }

    }

    redeemOrVerify();
  }, [userData]);

  // commented cause causing tryRedeemQr to be called multiple time
  // // // Also check for pending QR code when screen comes into focus
  // // Check pending QR only when navigated
  // useFocusEffect(
  //   React.useCallback(() => {
  //     console.log('NAVIGATED!')
  //     if (userData && userData.name) {
  //       // if user is not verified yet, redirect them to verification screen
  //       if( userData.user_profile_completed_at && !userData.user_verified_at ) {
  //         console.log("Has email and not verified");
  //         navigation.replace('Verification');
  //         return;
  //       } else {
  //         console.log('here');
  //         tryRedeemPendingQr();
  //       }
  //     }
  //   }, [navigation])
  // );

  useEffect(() => {
    async function checkAutoClaim() {
      // check if there is flag to auto claim pending QR
      const isAutoClaimPendingQR = await AsyncStorage.getItem('autoClaimPendingQR');
      if( !!isAutoClaimPendingQR ) {
        setProfileCompletedVisible(true);
      } else {
        setValidationResult(pendingQrValidationResult);
        setValidationModalVisible(true);
        loadIGBanner();
      }
    }

    if( pendingQrData && pendingQrValidationResult ) {
      checkAutoClaim();
    }
  }, [pendingQrData, pendingQrValidationResult])

  const closeWelcomeModal = React.useCallback(() => {
    setShowWelcomeModal(false);
  }, []);

  const handlePromoDetailClick = (promo) => {
    if (promo.type === 'estamp') {
      navigation.navigate('Estamp');
    } else if (promo.type === 'voucher') {
      const coupon = promo.data;
      navigation.navigate('CouponDetails', { coupon });
    }
  }
  
  const closeCouponHighlight = React.useCallback(() => {
    setCouponHighlight({
      visible: false,
      image: null
    });
  }, []);

  const formatPointSticker = (point) => {
    if( point < 10000 ) {
      return `${formatPoinNumber(point)}`;
    }
    if( point < 1000000 ) {
      return `${Math.floor(point/1000)}K+`;
    }
    
    return `${Math.floor(point/1000000)}M+`;
  }

  useEffect(() => {
    if (validationModalVisible) {
      showConfetti();
    }
  }, [validationModalVisible]);

  useEffect(() => {
    if (
      spinResultVisible
      && !['none', ''].includes((spinPrizeResult?.prize_type || ''))
    ) {
      showConfetti();
    }
  }, [spinResultVisible]);

  if (!fontsLoaded || !userData || !userData.name) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingContainerText}>Loading...</Text>
      </View>
    );
  }

  const spinPopupPrizes = spinPromo?.data?.prizes || [];
  const spinPopupSlice = spinPopupPrizes.length > 0 ? 360 / spinPopupPrizes.length : 0;
  const spinUiTheme = resolveSpinWheelTheme(spinPromo?.data?.ui_theme);
  const spinBtnGradient = spinButtonGradientColors(spinPromo?.data?.ui_theme);
  const spinLogoSize = resolveSpinLogoSize(spinPromo?.data?.logo_size);
  const spinLogoLayout = wheelLogoLayout(spinLogoSize, spinPopupWheelSize);
  const isRedeemSuccess = Boolean(
    redeemModal.result
    && !['invalid', 'already_used', 'already_scanned', 'expired', 'network', 'error', 'session_expired'].includes(redeemModal.result.type)
  );

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />
      
      {/* Header */}
      {/* <View style={styles.header}>
        <View style={styles.headerPattern}>
        </View>
        
     
        <View style={styles.statusBar}>
          <Text style={styles.statusTime}>9:41</Text>
          <View style={styles.statusIcons}>
            <Ionicons name="cellular" size={16} color="#000000" />
            <Ionicons name="wifi" size={16} color="#000000" style={{ marginLeft: 5 }} />
            <View style={styles.battery}>
              <View style={styles.batteryLevel} />
            </View>
          </View>
        </View>

      </View> */}

      <ScrollView
        style={styles.content}
        showsVerticalScrollIndicator={width > 1200}
        onScroll={(e) => setScrollY(e.nativeEvent.contentOffset.y)}
        scrollEventThrottle={16}
      >
        {/* User Profile Section */}
        <ImageBackground
          source={require('../../assets/top-background.png')}
          style={styles.profileSection}
          resizeMode="cover"
        >
          <View style={styles.topHeaderRow}>
            
            <TouchableOpacity 
              style={styles.zclubLogoContainer}
              activeOpacity={0.7}
              onPress={setShowWelcomeModal.bind(true)}
            >
              <View style={styles.zclubLogo}>
                <Image
                  source={require('../../assets/zoolyfe-club-new-white.webp')}
                  style={{ width: '100%', maxWidth: 130, aspectRatio: 1.89 }}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.zclubLogoBottomText}>zoolyfe.club</Text>
              <View style={styles.zclubLogoIcon}>
                <Ionicons name="information-circle-outline" size={12} color="#FFFFFF" />
              </View>
            </TouchableOpacity>
          </View>

          <View style={styles.bottomHeaderRow}>
            {userData.user_avatar ? (
              <Image 
                source={`${API_BASE_URL}/${userData.user_avatar}`}
                style={styles.avatar} 
                resizeMode='cover'
              />
            ) : (
              <View style={styles.avatarPlaceholder}>
                <Ionicons name="person" size={28} color="#F8FAFC" />
              </View>
            )}
            <View style={styles.profileTextContainer}>
              <View style={styles.profileButtons}>
                <NotificationBellButton
                  unreadCount={unreadNotifCount}
                  onPress={() => navigation.navigate('Notification')}
                />

                <TouchableOpacity 
                  style={styles.notificationButton}
                  onPress={() => navigation.navigate('Scan')}
                >
                  <Ionicons name="scan-outline" size={24} color="#F8FAFC" />
                </TouchableOpacity>
              </View>
              <Text style={styles.profileGreeting}>Halo {userData.name ? userData.name.split(' ')[0] : 'User'}!</Text>
            </View>
          </View>

          {/* Points and Stamps Cards */}
          <View style={styles.cardsContainer}>
            <TouchableOpacity 
              style={styles.pointCard}
              activeOpacity={0.7}
              onPress={() => {
                navigation.navigate('Points');
              }}
            >
              <View style={styles.pointEllipseBackground} />
              <View style={styles.cardContent}>
                <View style={styles.pointCoinContainer}>
                  <Image 
                    source={require('../../assets/icons/z-point-solid.svg')} 
                    style={{ 
                      width: 14, 
                      height: 14, 
                      tintColor: "#FFFFFF"
                    }}
                    resizeMode="contain"
                  />
                </View>
                <Text style={styles.cardTitle}>Point Reward</Text>
                <Text style={styles.pointsValue}>{formatPointSticker(userData.points)}</Text>
                <Text style={styles.pointsUnit}>pt</Text>
              </View>
            </TouchableOpacity>
          

            <TouchableOpacity 
              style={styles.stampCard}
              activeOpacity={0.7}
              onPress={() => {
                navigation.navigate('Estamp');
              }}
            >
              <View style={styles.ellipseBackground} />
              <View style={styles.cardContent}>
                <View style={styles.stampContainer}>
                  <Ionicons name="star" size={10} color="#F8FAFC" />
                </View>
                <Text style={styles.cardTitle}>E-Stamp</Text>
                <Text style={styles.pointsValue}>{formatPointSticker(userData.stamps)}</Text>
                <Text style={styles.pointsUnit}>Stickers</Text>
              </View>
            </TouchableOpacity>
          </View>
        </ImageBackground>

        {spinPendingClaims.length > 0 ? (
          <TouchableOpacity
            style={styles.spinPendingBanner}
            activeOpacity={0.85}
            onPress={() => navigation.navigate('SpinPrizeClaimHistory')}
          >
            <View style={styles.spinPendingBannerIcon}>
              <Ionicons name="gift" size={22} color="#D97706" />
            </View>
            <View style={styles.spinPendingBannerText}>
              <Text style={styles.spinPendingBannerTitle}>
                {spinPendingClaims.length === 1
                  ? '1 hadiah spin belum diklaim'
                  : `${spinPendingClaims.length} hadiah spin belum diklaim`}
              </Text>
              <Text style={styles.spinPendingBannerSub}>
                Ketuk untuk klaim sekarang di riwayat hadiah
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color="#D97706" />
          </TouchableOpacity>
        ) : null}

        {/* Promo Corner Section */}
        <View style={styles.promoCornerSection}>
          <View style={styles.promoHeader}>
            {/*<Text style={styles.promoCornerTitle}>Promo Info</Text>*/}
            {/* <Text style={styles.promoSubtitle}>Pilihan promo menarik yang bisa kamu tukarkan</Text> */}
            {/* <TouchableOpacity
              style={styles.promoHeaderButton}
              onPress={() => {
                window.open(API_BASE_URL, '_blank');
              }}
            >
              <Image
                source={require('../../assets/shop-now.png')}
                style={{ width: '100%', maxWidth: 110, aspectRatio: 110 / 92 }}
                resizeMode="contain"
              />
            </TouchableOpacity> */}
          </View>
          
          <View style={styles.promoGrid}>
            <SocialBannerFeed
              isAuthenticated
              currentUser={
                userData
                  ? {
                      id: Number(userData.user_id) || 0,
                      name: (userData.name || 'You').split(' ')[0],
                      avatar: userData.user_avatar ? `${API_BASE_URL}/${userData.user_avatar}` : null,
                    }
                  : { id: 0, name: 'You', avatar: null }
              }
              openBannerId={socialBannerDeepLink?.id}
              openBannerNonce={socialBannerDeepLink?.nonce}
              onOpenedBanner={() => {
                setSocialBannerDeepLink(null);
                void consumePendingSocialBannerId();
              }}
              onClosedBanner={releaseSocialBannerSpinHold}
            />
            {promoData.loading ? (
              <View style={styles.promoLoadingContainer}>
                <Text style={styles.promoLoadingText}>Loading promo...</Text>
              </View>
            ) : promoData.items.length > 0 ? (
              promoData.items.map((item, index) => {
                const hideVoucherCouponText = isPromoVoucherCouponKegunaan(item);
                return (
                  <View
                    key={item.id}
                  >
                    {item.isEmptyCoupon ? (
                      <TouchableOpacity 
                        style={styles.promoCard}
                        onPress={() => {
                          // if(item.isEmptyCoupon) {
                          if(URL.canParse(item.externalURL)) {
                            {window.open(item.externalURL, '_blank')}
                          } else {
                            setCouponHighlight({
                              visible: true,
                              image: item.highlightImage ? item.highlightImage : portraitDummyImage
                            })
                          }
                        }}
                      >
                        <Image 
                          source={item.image ? { uri: item.image } : {uri: dummyImage}}
                          style={{
                            ...styles.promoCardBackground,
                            ...styles.promoCardImage
                          }} 
                          resizeMode='cover'
                        />
                      </TouchableOpacity>
                    ) : (
                      <View 
                        style={styles.couponCard}
                      >
                        <TouchableOpacity 
                          style={styles.couponHeader}
                          onPress={() => handlePromoDetailClick(item)}
                        >
                          <Image 
                            source={item.image ? { uri: item.image } : {uri: dummyImage}}
                            style={styles.couponHeaderImage} 
                            resizeMode='cover'
                          />
                        </TouchableOpacity>
                        <View style={styles.couponContent}>
                          <Text style={styles.couponTitle}>{item.title}</Text>

                          <TouchableOpacity 
                            style={styles.couponButton}
                            onPress={() => handlePromoDetailClick(item)}
                          >
                            <Text style={styles.couponButtonText}>{`Lihat Detail`}</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}
                  </View>
                );
              })
            ) : null}
          </View>
        </View>



        <View style={styles.bottomSpacing} />
      </ScrollView>
      
      {/* Redeem Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={Boolean(redeemModal.visible && redeemModal.result)}
        onRequestClose={() => {
          closeRedeemModal();
          setFocusOnQR(false);
        }}
        onDismiss={clearRedeemModalResult}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, styles.validationModalContent]}>
            <ScrollView
              style={styles.validationScrollView}
              showsVerticalScrollIndicator={true}
              keyboardShouldPersistTaps="handled"
            >
              <LinearGradient
                colors={ !redeemModal.result 
                  ? ['#FF6B35', '#FF8E53'] 
                  : isRedeemSuccess 
                    ? ['#4CAF50', '#4CAF5080'] 
                    : ['#FF6B35', '#FF8E53'] }
                style={[styles.resultHeader, { padding: 0 }]}
              >
                <View style={styles.resultHeaderContainer}>
                  {!isRedeemSuccess && (
                    <View style={[styles.resultIcon, { marginBottom: 0 }]}>
                      {!redeemModal.result ? (
                        <HeroSolid.XCircle height="30" width="30" color="#FFFFFF" />
                      ) : isRedeemSuccess ? (
                        <HeroSolid.CheckBadge height="30" width="30" color="#FFFFFF" />
                      ) : (
                        <HeroSolid.XCircle height="30" width="30" color="#FFFFFF" />
                      )}
                    </View>
                  )}
                  <View style={styles.resultTitleWrapper}>
                    <Text style={[styles.resultTitle, { marginBottom: 0 }, !isRedeemSuccess && styles.resultTitleLeft]}>
                      {!redeemModal.result
                        ? 'Redeem Gagal'
                        : redeemModal.result.type === 'session_expired'
                          ? redeemModal.result.title
                          : isRedeemSuccess
                            ? (redeemModal.result.title || 'Redeem Berhasil!')
                            : 'Redeem Gagal'}
                    </Text>
                    {(
                      !redeemModal.result
                      || !!redeemModal.result.message
                      || (redeemModal.result.type !== 'coupon' && redeemModal.result.type !== 'all')
                    ) && (
                      <Text style={[styles.resultMessage, !isRedeemSuccess && styles.resultMessageLeft]}>
                        {!redeemModal.result
                          ? 'Gagal memproses redeem. Silakan coba lagi.'
                          : redeemModal.result.message
                            || (isRedeemSuccess
                              ? 'QR Code berhasil di-redeem!'
                              : 'Terjadi kesalahan saat redeem')}
                      </Text>
                    )}
                  </View>
                </View>
                {isRedeemSuccess ? (
                  <View style={styles.bannerWrapper}>
                    {bannerWrapper}
                  </View>
                ) : null}
              </LinearGradient>

              {redeemModal.result?.errorData && (
                <View style={[styles.resultDetails, styles.expiredContainer]}>
                  {redeemModal.result?.type === 'expired' && (
                    <>
                      <Text style={[styles.sectionTitle, styles.expiredText]}>Periode klaim voucher telah berakhir{redeemModal.result?.errorData?.expiredDate ? ` pada` : ``}</Text>
                      
                      {redeemModal.result?.errorData?.expiredDate && (
                        <Text style={[styles.sectionTitle, styles.expiredText, styles.expiredDateText]}>{redeemModal.result?.errorData?.expiredDate.toLocaleDateString('id-ID', {day: 'numeric', month: 'long', year: 'numeric'})}</Text>
                      )}

                      <Text style={[styles.sectionTitle, styles.expiredText]}>Nantikan program berikutnya.</Text>
                    </>
                  )}
                </View>
              )}
              
              <View style={styles.resultDetails}>
                {(redeemModal.result?.type === 'all' || redeemModal.result?.type.includes('points')) && redeemModal.result.rewards?.points > 0 && (
                  <View style={styles.rewardInfo}>
                    <Text style={styles.rewardLabel}>Points Earned</Text>
                    <View style={styles.rewardValueRow}>
                      <Text style={styles.rewardValue}>
                        +{formatPoinNumber(redeemModal.result.rewards?.points)} {redeemModal.result.rewards?.points === 1 ? 'Point' : 'Points'}
                      </Text>
                      {qrRewardSourceName(redeemModal.result?.qrCode) ? (
                        <Text style={styles.rewardBrandText}> dari <Text style={{fontWeight: 700}}>{qrRewardSourceName(redeemModal.result.qrCode)}</Text></Text>
                      ) : null}
                    </View>
                  </View>
                )}
                
                {(redeemModal.result?.type === 'all' || redeemModal.result?.type.includes('stamp')) && redeemModal.result.rewards?.stamps > 0 && (
                  <View style={styles.rewardInfo}>
                    <Text style={styles.rewardLabel}>E-Stamps Collected</Text>
                    <View style={styles.rewardValueRow}>
                      <Text style={styles.rewardValue}>
                        +{redeemModal.result.rewards?.stamps} {redeemModal.result.rewards?.stamps === 1 ? 'e-stamp' : 'e-stamps'}
                      </Text>
                      {qrRewardSourceName(redeemModal.result?.qrCode) ? (
                        <Text style={styles.rewardBrandText}> dari <Text style={{fontWeight: 700}}>{qrRewardSourceName(redeemModal.result.qrCode)}</Text></Text>
                      ) : null}
                    </View>
                  </View>
                )}
                
                {(redeemModal.result?.type === 'all' || redeemModal.result?.type.includes('coupon') ) && redeemModal.result.coupon && (
                  <View style={styles.rewardAllContainer}>
                    <View style={styles.rewardAllSection}>
                      <View style={styles.rewardAllHeader}>
                        <HeroSolid.Gift height="20" width="20" color="#FF6B35" style={styles.rewardAllIcon} />
                        <Text style={styles.rewardAllSectionTitle}>Reward Kamu</Text>
                      </View>
                      <View style={styles.horizontalLine} />
                      {!!redeemModal.result.paygiftClaimPending && (
                        <Text style={styles.rewardAllValidityNote}>
                          {(redeemModal.result.paygiftClaimModalUi
                            || getPaygiftClaimModalUi(redeemModal.result.coupon)
                          ).queueNotice}
                        </Text>
                      )}
                      <Text style={styles.rewardAllVoucherName}>{redeemModal.result.coupon.nama_kupon}</Text>
                      <Text style={styles.rewardAllValidityNote}>Voucher berlaku 30 hari setelah diklaim.</Text>
                      {/*{`Voucher berlaku hingga: ${new Date(redeemModal.result.coupon.tanggal_berakhir).toLocaleDateString('id-ID', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}`}*/}
                    </View>
                    <View style={styles.horizontalLine} />
                    <View style={styles.rewardAllSection}>
                      <Text style={styles.rewardAllStatusTitle}>Status Voucher</Text>
                      <Text style={styles.rewardAllStatusValue}>
                        {redeemModal.result.paygiftClaimPending
                          ? (redeemModal.result.paygiftClaimModalUi
                              || getPaygiftClaimModalUi(redeemModal.result.coupon)
                            ).statusLabel
                          : 'Siap digunakan'}
                      </Text>
                      <Text style={styles.rewardAllUsageNote}>
                        {redeemModal.result.paygiftClaimPending
                          ? (redeemModal.result.paygiftClaimModalUi
                              || getPaygiftClaimModalUi(redeemModal.result.coupon)
                            ).usageNote
                          : 'Voucher dapat digunakan melalui menu Kupon - Shopping Coupon'}
                      </Text>
                    </View>
                  </View>
                )}
                
                {/* {redeemModal.result?.type !== 'coupon' && redeemModal.result?.type !== 'all' && redeemModal.result?.userBalance && ( */}
                {redeemModal.result?.type !== 'coupon' && redeemModal.result?.userBalance && (
                  <View style={styles.userStats}>
                    <Text style={styles.statsTitle}>Your Current Balance</Text>
                    <View style={styles.statsRow}>
                      {redeemModal.result?.userBalance?.totalPoints !== 0 && (
                        <View style={styles.statItem}>
                          <PointIcon size={20} color={"#FF6B35"} />
                          <Text style={styles.statText}>{formatPoinNumber(redeemModal.result.userBalance.totalPoints)} points</Text>
                        </View>
                      )}
                      {redeemModal.result?.userBalance?.totalStamps !== 0 && (
                        <View style={styles.statItem}>
                          <HeroSolid.Bookmark height="20" width="20" color="#4CAF50" />
                          <Text style={styles.statText}>{redeemModal.result.userBalance.totalStamps} stamps</Text>
                        </View>
                      )}
                    </View>
                  </View>
                )}
              </View>
              
              <ScanSuccessNextActions
                scanResult={redeemModal.result}
                loading={referralCheckLoading}
                primaryColor={redeemModal.result?.color || '#FF6B35'}
                onLihatPoin={async () => {
                  if (pendingQrData) {
                    await deletePendingQR(pendingQrData);
                  }
                  setPendingQrData(null);
                  await AsyncStorage.removeItem('autoClaimPendingQR');
                  setFocusOnQR(false);
                  closeRedeemModal();
                  navigation.navigate('TukarPoin', { tab: 'promo' });
                }}
                onPrimary={async () => {
                  const scanResult = redeemModal.result;
                  if (isPointsScanSuccessWithoutCoupon(scanResult)) {
                    const pendingQRsForScanAgain = await getPendingQRs();
                    const isAutoClaimPendingQR = await AsyncStorage.getItem('autoClaimPendingQR');
                    if (!(pendingQRsForScanAgain.length > 0 && !!isAutoClaimPendingQR)) {
                      if (pendingQrData) {
                        await deletePendingQR(pendingQrData);
                      }
                      setPendingQrData(null);
                      await AsyncStorage.removeItem('autoClaimPendingQR');
                      setFocusOnQR(false);
                      closeRedeemModal();
                      navigation.navigate('Scan');
                      return;
                    }
                  }

                  const openClaimedCouponOrList = () => {
                    closeRedeemModal();
                    if (shouldNavigateToCouponDetails(scanResult)) {
                      navigateToClaimedCouponDetails(navigation, scanResult.coupon, scanResult.kawanActivation);
                      return;
                    }
                    navigation.navigate('Coupons');
                  };

                  // as latest request that new phone only user can get all reward they ever scanned before completing their profile
                  // then we need to re call tryRedeemPendingQr here until pending qr array all cleared
                  if(!!pendingQrData) {
                    await deletePendingQR(pendingQrData);
                  }
                  setPendingQrData(null);
                  const pendingQRs = await getPendingQRs();
                  const isAutoClaimPendingQR = await AsyncStorage.getItem('autoClaimPendingQR');

                  // if there is another pending qr stored, and it is from phone only user,
                  // directly process that qr after clicking continue button
                  if( pendingQRs.length > 0 && !!isAutoClaimPendingQR ) {
                    closeRedeemModal();
                    await tryRedeemPendingQr();
                    return;
                  } else {
                    await AsyncStorage.removeItem('autoClaimPendingQR');
                  }

                  // remove focus on QR to make space for spinning wheel or another feature needs to be highlighted
                  setFocusOnQR(false);

                  const wasSuccess = redeemModal.result && !['invalid', 'already_used', 'expired', 'network', 'error', 'session_expired'].includes(redeemModal.result?.type);
                  if (!wasSuccess) {
                    closeRedeemModal();
                    if( userData && !userData.user_profile_completed_at ) {
                      if( pendingQRs.length == 0 )
                        doLogout();
                      else 
                        window.location.reload();
                    }
                    return;
                  }
                  if (shouldNavigateToCouponDetails(scanResult)) {
                    setPolModal({ visible: false, step: 'join', promotions: [], id_promosi: null, nama_promosi: null });
                    navigateToClaimedCouponFromRedeem(scanResult);
                    return;
                  }
                  if (!shouldOfferPolReferralPopup(redeemModal.result)) {
                    if( 
                      pendingQRs.length == 0
                      && shouldNavigateToCouponDetails(scanResult)
                    ) {
                      closeRedeemModal();
                      openClaimedCouponOrList();
                    } else {
                      closeRedeemModal();
                    }
                    return;
                  }
                  if (polModal.step === 'pick' && polModal.promotions?.length) {
                    closeRedeemModal();
                    setPolModal((prev) => ({ ...prev, visible: true }));
                    return;
                  }
                  if (polModal.id_promosi) {
                    closeRedeemModal();
                    setPolModal((prev) => ({ ...prev, visible: true }));
                    return;
                  }
                  setReferralCheckLoading(true);
                  try {
                    const refOpts = referralOptionsAfterScan(scanResult);
                    if (!refOpts) {
                      closeRedeemModal();
                      if( 
                        pendingQRs.length == 0
                        && shouldNavigateToCouponDetails(scanResult)
                      ) {
                        openClaimedCouponOrList();
                      }
                      return;
                    }
                    const r = await getReferralAvailable(refOpts);
                    closeRedeemModal();
                    const promos = normalizeReferralPromotions(r);
                    if (promos.length === 1) {
                      setPolModal({
                        visible: true,
                        step: 'join',
                        promotions: [],
                        id_promosi: promos[0].id_promosi,
                        nama_promosi: promos[0].nama_promosi || '',
                      });
                    } else if (promos.length > 1) {
                      setPolModal({
                        visible: true,
                        step: 'pick',
                        promotions: promos,
                        id_promosi: null,
                        nama_promosi: null,
                      });
                    } else {
                      if( 
                        pendingQRs.length == 0
                        && shouldNavigateToCouponDetails(scanResult)
                      ) {
                        openClaimedCouponOrList();
                        return;
                      }

                      if (__DEV__) console.log('POL tidak ditampilkan – referral-available:', r);
                      Alert.alert(
                        'Tidak ada penawaran POL untuk batch QR ini',
                        'Di admin, hubungkan promo Referral Code ke batch QR ini, atau pastikan Anda memenuhi syarat dan kuota POL. Program lain yang bisa diikuti tetap tampil di menu Profil → Kode Referral Saya.',
                        [{ text: 'OK', onPress: openClaimedCouponOrList }],
                        { cancelable: false }
                      );
                      return;
                    }
                  } catch (_) {
                    closeRedeemModal();
                    Alert.alert(
                      'Program referral tidak tersedia',
                      'Tidak dapat memeriksa program POL. Cek koneksi atau coba lagi nanti di menu Profil → Kode Referral Saya.',
                      [{ text: 'OK', onPress: openClaimedCouponOrList }],
                      { cancelable: false }
                    );
                    return;
                  } finally {
                    setReferralCheckLoading(false);
                  }
                }}
              />
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Join-POL modal after successful coupon claim */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={polModal.visible}
        onRequestClose={() => {
          if (!joinPolLoading) {
            setPolModal({ visible: false, step: 'join', promotions: [], id_promosi: null, nama_promosi: null });
          }
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.modalOverlay}
        >
          <View style={styles.modalContainer}>
            <LinearGradient
              colors={['#FF6B35', '#FF8E53']}
              style={styles.resultHeader}
            >
              <View style={styles.resultIcon}>
                <Ionicons name="people" size={36} color="#FFFFFF" />
              </View>
              <Text style={[styles.resultTitle, { color: '#FFFFFF' }]}>
                {polModal.step === 'pick' ? 'Pilih program POL' : 'Gabung sebagai POL?'}
              </Text>
              {polModal.step === 'join' && polModal.nama_promosi ? (
                <Text style={styles.resultMessage}>{polModal.nama_promosi}</Text>
              ) : null}
            </LinearGradient>
            <View style={styles.resultDetails}>
              {polModal.step === 'pick' && polModal.promotions?.length ? (
                <>
                  <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 12, lineHeight: 22 }}>
                    Beberapa program sedang aktif. Pilih salah satu untuk lanjut membuat kode referral.
                  </Text>
                  <ScrollView style={{ maxHeight: 280 }} keyboardShouldPersistTaps="handled">
                    {polModal.promotions.map((p) => (
                      <TouchableOpacity
                        key={p.id_promosi}
                        style={{
                          paddingVertical: 14,
                          paddingHorizontal: 12,
                          borderWidth: 1,
                          borderColor: '#E2E8F0',
                          borderRadius: 10,
                          marginBottom: 10,
                          backgroundColor: '#F8FAFC',
                        }}
                        onPress={() => {
                          setPolModal((prev) => ({
                            ...prev,
                            step: 'join',
                            id_promosi: p.id_promosi,
                            nama_promosi: p.nama_promosi || '',
                          }));
                        }}
                      >
                        <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A' }}>{p.nama_promosi}</Text>
                        {p.scope_line ? (
                          <Text style={{ fontSize: 12, color: '#64748B', marginTop: 4 }} numberOfLines={3}>
                            {p.scope_line}
                          </Text>
                        ) : null}
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                  <TouchableOpacity
                    style={[styles.continueButton, { backgroundColor: '#94A3B8', marginTop: 8 }]}
                    onPress={() => {
                      setPolModal({ visible: false, step: 'join', promotions: [], id_promosi: null, nama_promosi: null });
                    }}
                  >
                    <Text style={styles.continueButtonText}>Batal</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
              <Text style={{ fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 16, lineHeight: 22 }}>
                Kode referral dibuat otomatis dari brand program (atau nama program), nama depan profil Anda, dan 3–4
                digit terakhir nomor HP. Setelah gabung, bagikan link ke teman dan klaim reward saat syarat terpenuhi.
              </Text>
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity
                  style={[styles.continueButton, { backgroundColor: '#94A3B8', flex: 1, margin: 0 }]}
                  onPress={() => {
                    setPolModal({ visible: false, step: 'join', promotions: [], id_promosi: null, nama_promosi: null });
                    navigation.navigate('Coupons');
                  }}
                  disabled={joinPolLoading}
                >
                  <Text style={styles.continueButtonText}>Nanti</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.continueButton, {
                    backgroundColor: joinPolLoading ? '#FF8E53' : '#FF6B35',
                    flex: 1,
                    margin: 0,
                    opacity: joinPolLoading ? 0.85 : 1,
                  }]}
                  onPress={async () => {
                    if (!polModal.id_promosi) return;
                    setJoinPolLoading(true);
                    try {
                      await joinPromotion(polModal.id_promosi);
                      setPolModal({ visible: false, step: 'join', promotions: [], id_promosi: null, nama_promosi: null });
                      navigation.navigate('Referral');
                    } catch (e) {
                      Alert.alert('Info', e.message || 'Gagal gabung POL. Coba lagi.');
                    } finally {
                      setJoinPolLoading(false);
                    }
                  }}
                  disabled={joinPolLoading}
                >
                  {joinPolLoading ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.continueButtonText}>Gabung Sekarang</Text>
                  )}
                </TouchableOpacity>
              </View>
                </>
              )}
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Validation Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={validationModalVisible}
        onRequestClose={()=>{}}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.validationModalContent]}>
            {validationResult && (
              <ScrollView
                style={[styles.validationScrollView, {position: 'relative'}]}
                showsVerticalScrollIndicator={true}
                keyboardShouldPersistTaps="handled"
              >
                {confettiWrapper}
                <View
                  style={[styles.resultHeader, styles.validationResultHeader]}
                >
                  {validationResult.qrCode?.brand?.logo && (
                    <View style={styles.brandLogoContainer}>
                      <Image
                        source={{ uri: validationResult.qrCode.brand.logo }}
                        style={styles.brandLogo}
                        resizeMode="contain"
                      />
                    </View>
                  )}
                  {!!userData && !!userData.user_profile_completed_at ? (
                    <>
                      <Text style={[styles.resultTitle, styles.validationResultTitle]}>Selamat <Text style={{fontSize: 16, marginLeft: 2}}>🎉</Text></Text>
                      <Text style={[styles.resultMessage, styles.validationResultTitle]}>QR Code kamu berhasil diverifikasi dan reward sudah siap untuk diklaim</Text>
                    </>
                  ) : (
                    <Text style={[styles.resultTitle, styles.validationResultTitle, {marginBottom: 0}]}>Selamat, Kamu sudah menjadi member Zoolyfe <Text style={{fontSize: 16, marginLeft: 2}}>🎉</Text></Text>
                  )}
                </View>
                  
                <View style={{
                  ...styles.horizontalDashedLine,
                  marginVertical: 0,
                  marginHorizontal: 16,
                }}></View>
                
                <View style={styles.resultDetails}>

                  {/* Rewards Information */}
                  <View>
                    <Text style={styles.sectionTitle}>Kamu mendapatkan</Text>
                    
                    <View style={styles.rewardsSection}>
                      {(validationResult.rewards?.points || 0) > 0 && (
                        <View style={styles.rewardItem}>
                          <PointIcon size={20} color={"#F59E0B"} />
                          <View style={styles.pointWrapper}>
                            <Text style={styles.rewardText}>
                              {scanPointsFromLabel(validationResult.rewards.points, validationResult.qrCode)}
                            </Text>
                            <Text style={styles.pointConvertion}>{`(≈ Rp. ${formatPoinNumber(Number(validationResult.rewards.points || 0) * 100)})`}</Text>
                          </View>
                        </View>
                      )}
                      
                      {(validationResult.rewards?.stamps || 0) > 0 && (
                        <View style={styles.rewardItem}>
                          <HeroSolid.Bookmark height="20" width="20" color="#4CAF50" />
                          <Text style={styles.rewardText}>
                            {scanStampsFromLabel(validationResult.rewards.stamps, validationResult.qrCode)}
                          </Text>
                        </View>
                      )}
                      
                      {validationResult.rewards?.hasCoupon && validationResult.couponDetails && (
                        <View style={styles.rewardItem}>
                          <HeroSolid.Ticket height="20" width="20" color="#9C27B0" />
                          <Text style={styles.rewardText}>
                            {validationResult.couponDetails.nama_kupon}
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>
                  
                </View>

                {(validationResult.rewards?.points || 0) > 0 && (<PointRewardHint />)}

                <View style={[styles.resultDetails, {paddingTop: 0}]}>

                  <ScanClaimPrizeList
                    titleStyle={styles.sectionTitle}
                    itemStyle={styles.rewardItem}
                    textStyle={styles.rewardText}
                  />
                  
                </View>
                
                {/* Regular User Action Buttons */}
                {(userData && userData.user_profile_completed_at) && (
                  <View style={styles.validationActions}>
                    <TouchableOpacity
                      style={[styles.validationButton, styles.cancelButton]}
                      onPress={() => {
                        setValidationModalVisible(false);
                        setFocusOnQR(false);
                      }}
                    >
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    
                    <TouchableOpacity
                      style={[styles.validationButton, styles.acceptButton]}
                      onPress={handleAcceptReward}
                    >
                      <Text style={styles.acceptButtonText}>Klaim Reward</Text>
                    </TouchableOpacity>
                  </View>
                )}

                {/* Phone-Only User's Action Buttons */}
                {(userData && !userData.user_profile_completed_at) && (
                  <View style={styles.validationActionsVertical}>
                    <TouchableOpacity
                      style={[styles.validationButtonWide, styles.completeButton]}
                      onPress={() => {
                        setValidationModalVisible(false);
                        navigation.navigate('ProfileForm');
                      }}
                    >
                      <Text style={styles.completeButtonText}>Klaim Sekarang</Text>
                    </TouchableOpacity>
                    
                    {/*<TouchableOpacity
                      style={[styles.validationButtonWide, styles.stopButton]}
                      onPress={() => {
                        // if user cancel here, count is as log out
                        doLogout();
                      }}
                    >
                      <Text style={styles.stopButtonText}>Batalkan</Text>
                    </TouchableOpacity>*/}
                  </View>
                )}

                <View style={styles.resultDetails}>
                  {/* QR Code Information */}
                  <View style={styles.qrInfoSection}>
                    <Text style={[styles.sectionTitle, styles.validationResultMiniSectionTitle]}>Voucher Information</Text>
                    <Text style={[styles.qrName, styles.validationResultMiniSectionName]}>
                      {(validationResult.qrCode?.nama || '').replace(/\s*-\s*\d+$/, '')}
                    </Text>
                    {!!validationResult.qrCode.deskripsi && (
                      <Text style={[styles.qrDescription, styles.validationResultMiniSectionDescription]}>{validationResult.qrCode.deskripsi}</Text>
                    )}
                  </View>

                  {validationResult.rewards?.hasCoupon && validationResult.couponDetails && (
                    <>
                      <View style={{
                        ...styles.horizontalLine,
                        marginVertical: 8
                      }}></View>
                      <View style={styles.qrInfoSection}>
                        <Text style={[styles.sectionTitle, styles.validationResultMiniSectionTitle]}>Coupon Details</Text>

                        {validationResult.couponDetails.tipe_kupon === 'diskon_persen' && (
                          <Text style={[styles.qrDescription, styles.validationResultMiniSectionName]}>{validationResult.couponDetails.nilai_diskon}% Discount</Text>
                        )}
                        {validationResult.couponDetails.tipe_kupon === 'diskon_nominal' && (
                          <Text style={[styles.qrDescription, styles.validationResultMiniSectionName]}>Rp {validationResult.couponDetails.nilai_diskon.toLocaleString()} Off</Text>
                        )}

                        <Text style={[styles.qrDescription, styles.validationResultMiniSectionName]}>{validationResult.couponDetails.deskripsi}</Text>
                        <Text style={[styles.qrDescription, styles.validationResultMiniSectionDescription]}>
                          {`Berlaku hingga: ${new Date(validationResult.couponDetails.tanggal_berakhir).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}`}
                        </Text>
                      </View>
                    </>
                  )}
                </View>

                {/* IG Banner */}
                {igBanner.data ? (
                  <View style={styles.bannerContainer}>
                    <View style={styles.banner}>
                      <Image 
                        style={styles.bannerImage}
                        source={{uri: igBanner.data.background}}
                        resizeMode='cover'
                      ></Image>
                      <View style={styles.bannerOverlay}></View>
                      <View style={styles.bannerContent}>
                        <Image 
                          style={styles.bannerLogo}
                          source={{uri: igBanner.data.logo}}
                          resizeMode='contain'
                        ></Image>
                        <Text style={styles.bannerTitle}>{igBanner.data.title}</Text>
                        <View style={styles.bannerButtonContainer}>
                          {(igBanner.data.urls || []).map((url, index) => {
                            return (
                              <TouchableOpacity 
                                key={index}
                                style={styles.bannerButton}
                                onPress={() => window.open(url.url, '_blank')}
                              >
                                <FontAwesome5
                                  style={styles.bannerButtonIcon}
                                  name={url.type == "X" ? "twitter" : url.type.toLowerCase()} 
                                />
                                <Text style={styles.bannerButtonText}>{url.title}</Text>
                              </TouchableOpacity>
                            )
                          })}
                        </View>
                      </View>
                    </View>
                  </View>
                ) : null}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* QR Processing Loading Overlay */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={qrProcessing}
        onRequestClose={() => {}}
      >
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingContent}>
            <Ionicons name="scan" size={40} color="#FF6B35" />
            <Text style={styles.loadingText}>Processing QR Code...</Text>
          </View>
        </View>
      </Modal>

      {/* Information Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={incompleteModalVisible}
        onRequestClose={()=>{}}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.validationModalContent]}>
            <ScrollView
              style={styles.validationScrollView}
              showsVerticalScrollIndicator={true}
              keyboardShouldPersistTaps="handled"
            >
              <View
                style={[styles.resultHeader, styles.validationResultHeader]}
              >
                <Text style={[styles.resultTitle, styles.validationResultTitle, {marginBottom: 0}]}>
                  {hasPendingQR ? `Hadiahmu telah tersimpan!` : `Profilmu belum lengkap`}
                </Text>
              </View>
                
              <View style={{
                ...styles.horizontalDashedLine,
                marginVertical: 0,
                marginHorizontal: 16,
              }}></View>

              <View style={styles.resultDetails}>
                <Text style={[styles.sectionTitle, {textAlign: 'center'}]}>
                  {hasPendingQR ? `Hadiah masih dapat diklaim selama program masih berjalan` : `Lengkapi profilmu sekali, dan nikmati hadiah-hadiah dari Zoolyfe!`}
                </Text>
              </View>
              
              {/* Phone-Only User's Action Buttons */}
              <View style={[styles.validationActionsVertical, {paddingBottom: 16}]}>
                <TouchableOpacity
                  style={[styles.validationButtonWide, styles.completeButton]}
                  onPress={() => {
                    setIncompleteModalVisible(false);
                    navigation.navigate('ProfileForm');
                  }}
                >
                  <Text style={styles.completeButtonText}>
                    {hasPendingQR ? `Lengkapi Profil & Klaim` : `Lengkapi Profil`}
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[styles.validationButtonWide, styles.stopButton]}
                  onPress={() => {
                    // if user cancel here, count is as log out
                    doLogout();
                  }}
                >
                  <Text style={styles.stopButtonText}>Nanti Saja</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent={true}
        visible={!!spinPromo.visible && !!spinPromo.data && !focusOnQR && !socialBannerHoldSpin}
        onRequestClose={closeSpinPopup}
      >
        <View style={styles.spinModalOverlay}>
          <View style={[styles.spinModalCard, { maxHeight: spinPopupCardMaxHeight }]}>
            <TouchableOpacity
              onPress={closeSpinPopup}
              style={styles.spinCloseBtn}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="close" size={24} color="#1E293B" />
            </TouchableOpacity>
            <ScrollView
              style={styles.spinModalScroll}
              contentContainerStyle={styles.spinModalScrollContent}
              showsVerticalScrollIndicator={false}
              bounces={false}
              keyboardShouldPersistTaps="handled"
            >

            {/* ── Title group (logo + judul + alamat + badge) ── */}
            <View style={styles.spinTitleGroup}>
              <Image
                source={require('../../assets/zoolyfe-club-logo-new.jpeg')}
                style={styles.spinTitleLogo}
                resizeMode="contain"
              />
              <Text style={styles.spinHeading}>Spin Wheel Harian 🎉</Text>
              <Text style={styles.spinClubUrl}>zoolyfe.club</Text>
              {(spinPromo?.data?.max_spin_per_user || 0) > 0 && (
                <View style={[
                  spinIsExhausted ? styles.spinBadgeSoldOut : styles.spinBadge,
                  spinIsExhausted
                    ? { backgroundColor: spinUiTheme.badge_soldout_bg }
                    : { backgroundColor: spinUiTheme.badge_bg },
                ]}>
                  <Text style={[
                    spinIsExhausted ? styles.spinBadgeSoldOutText : styles.spinBadgeText,
                    { color: spinIsExhausted ? spinUiTheme.badge_soldout_text : spinUiTheme.badge_text },
                  ]}>
                    {spinBadgeLabel}
                  </Text>
                </View>
              )}
            </View>

            {/* ── Wheel area — urutan & overlap sama zoolife (landing): pointer dulu, lalu roda ── */}
            <View style={[styles.spinWheelArea, { width: spinPopupWheelSize }]}>
              <View
                style={[
                  styles.spinPointerWrap,
                  { marginBottom: Math.round(-26 * (spinPopupWheelSize / WHEEL_SIZE)) },
                ]}
                pointerEvents="none"
              >
                <PointerSvg theme={spinUiTheme} idPrefix="spinMainPtr" />
              </View>
              <View style={[
                styles.spinWheelOuterShadow,
                {
                  width: spinPopupWheelSize,
                  height: spinPopupWheelSize,
                  borderRadius: spinPopupWheelCx,
                  shadowColor: spinUiTheme.slice_a,
                },
              ]} pointerEvents="none">
                <View style={[
                  styles.spinWheelInnerClip,
                  {
                    width: spinPopupWheelSize,
                    height: spinPopupWheelSize,
                    borderRadius: spinPopupWheelCx,
                  },
                ]}>
                  <Animated.View style={{ transform: [{ rotate: spinPopupRotate }] }}>
                    <WheelSvg
                      prizes={spinPopupPrizes}
                      theme={spinUiTheme}
                      gradPrefix="swm"
                      size={spinPopupWheelSize}
                      logoSize={spinLogoSize}
                    />
                  </Animated.View>
                </View>
                <View style={[
                  styles.spinCenterLogoWrap,
                  {
                    width: spinLogoLayout.wrap,
                    height: spinLogoLayout.wrap,
                    borderRadius: spinLogoLayout.wrap / 2,
                    top: spinPopupWheelCx - spinLogoLayout.wrap / 2,
                    left: spinPopupWheelCx - spinLogoLayout.wrap / 2,
                    borderWidth: spinLogoLayout.borderW,
                    borderColor: spinUiTheme.hub_border,
                    backgroundColor: spinUiTheme.wheel_bg_from,
                  },
                ]} pointerEvents="none">
                  <Image
                    source={{ uri: spinPromo?.data?.logo_url }}
                    style={{
                      width: spinLogoLayout.logo,
                      height: spinLogoLayout.logo,
                    }}
                    resizeMode="contain"
                  />
                </View>
              </View>
            </View>

            {/* ── Button area: sold-out vs normal ── */}
            {spinIsExhausted ? (
              <View style={styles.spinSoldOutBtnWrap}>
                {/* Gradient sama seperti "Putar Sekarang", tapi opacity 0.5 dan tidak bisa diklik */}
                <View style={styles.spinSoldOutBtnClip} pointerEvents="none">
                  <LinearGradient
                    colors={spinBtnGradient}
                    start={{ x: 0, y: 0.5 }}
                    end={{ x: 1, y: 0.5 }}
                    style={[styles.spinSoldOutBtn, { opacity: 0.5 }]}
                  >
                    <Ionicons name="time" size={16} color={spinUiTheme.button_text} style={{ marginRight: 6 }} />
                    <Text style={[styles.spinSoldOutBtnText, { color: spinUiTheme.button_text }]}>
                      {spinUsesDailyQuota ? 'Tunggu Besok di zoolyfe.club' : 'Kesempatan Habis'}
                    </Text>
                  </LinearGradient>
                </View>
                {spinUsesDailyQuota ? (
                  <View style={styles.spinCountdownWrap}>
                    <Ionicons name="time" size={13} color="#94A3B8" style={{ marginRight: 4 }} />
                    <Text style={styles.spinCountdownLabel}>Spin berikutnya dalam </Text>
                    <CountdownTimer style={styles.spinCountdownTime} />
                  </View>
                ) : null}
              </View>
            ) : null}

            {/* ── Button (tanpa shadow). Pressable + LinearGradient: TouchableOpacity sering tidak dapat onPress di RN + expo-linear-gradient ── */}
            {!spinIsExhausted && <View style={styles.spinNowBtnWrap}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Putar Sekarang"
                disabled={spinPopupSpinning}
                onPress={() => {
                  void openSpinProgram();
                }}
                style={({ pressed }) => [
                  styles.spinNowBtnTouchable,
                  pressed && !spinPopupSpinning && { opacity: 0.92 },
                ]}
              >
                <View style={styles.spinNowBtnClip}>
                  {spinPopupSpinning ? (
                    <View style={[styles.spinNowGradient, { backgroundColor: '#94A3B8' }]}>
                      <AnimatedDots />
                    </View>
                  ) : (
                    <LinearGradient
                      colors={spinBtnGradient}
                      start={{ x: 0, y: 0.5 }}
                      end={{ x: 1, y: 0.5 }}
                      style={styles.spinNowGradient}
                    >
                      <View style={styles.spinNowContent}>
                        <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                          <Path d="M12 2L13.09 8.26L19 6L14.74 10.74L21 12L14.74 13.26L19 18L13.09 15.74L12 22L10.91 15.74L5 18L9.26 13.26L3 12L9.26 10.74L5 6L10.91 8.26Z" fill={spinUiTheme.button_text}/>
                          <Path d="M20 2L20.6 4.4L23 5L20.6 5.6L20 8L19.4 5.6L17 5L19.4 4.4Z" fill={spinUiTheme.button_text}/>
                          <Path d="M4 17L4.6 19.4L7 20L4.6 20.6L4 23L3.4 20.6L1 20L3.4 19.4Z" fill={spinUiTheme.button_text}/>
                        </Svg>
                        <Text style={[styles.spinNowText, { color: spinUiTheme.button_text }]}>Putar Sekarang</Text>
                        <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                          <Path d="M12 2L13.09 8.26L19 6L14.74 10.74L21 12L14.74 13.26L19 18L13.09 15.74L12 22L10.91 15.74L5 18L9.26 13.26L3 12L9.26 10.74L5 6L10.91 8.26Z" fill={spinUiTheme.button_text}/>
                          <Path d="M20 2L20.6 4.4L23 5L20.6 5.6L20 8L19.4 5.6L17 5L19.4 4.4Z" fill={spinUiTheme.button_text}/>
                          <Path d="M4 17L4.6 19.4L7 20L4.6 20.6L4 23L3.4 20.6L1 20L3.4 19.4Z" fill={spinUiTheme.button_text}/>
                        </Svg>
                      </View>
                    </LinearGradient>
                  )}
                </View>
              </Pressable>
            </View>}

            {/* ── Terms (no. 1) ── */}
            <Text style={styles.spinTermsText}>
              {'Dengan memutar '}
              <Text style={{ fontStyle: 'italic' }}>spinning wheel</Text>
              {' ini, kamu menyetujui '}
              <Text style={styles.spinTermsBold}>Syarat &amp; Ketentuan</Text>
              {' program Zoolyfe Club. Hadiah barang wajib diklaim dalam hari ini juga. '}
            </Text>

            {/* ── Social box (no. 2 — paling bawah) ── */}
            {(() => {
              const d = spinPromo?.data ?? {};
              const DEFAULT_IG_URL = 'https://www.instagram.com/zoolyfe.id?igsh=MXJhMXA4NTlnaXg1OQ==';
              const socialLogoUri = d.social_logo_url || SOCIAL_BOX_LOGO_FALLBACK;
              const igLabel   = d.social_ig_label || '@zoolyfe';
              const igUrl     = d.social_ig_url   || DEFAULT_IG_URL;
              const ttLabel   = d.social_tt_label || '@zoolyfe';
              const ttUrl     = d.social_tt_url   || 'https://www.tiktok.com/@zoolyfe.id?_r=1&_t=ZS-969ooqPAyOK';
              return (
                <View style={styles.spinSocialBox}>
                  <Image
                    source={{ uri: socialLogoUri }}
                    style={styles.spinSocialLogo}
                    resizeMode="contain"
                  />
                  <Text style={styles.spinSocialTitle}>Follow kami untuk info promo terbaru!</Text>
                  <View style={styles.spinSocialBtns}>
                    <TouchableOpacity
                      style={styles.spinSocialBtn}
                      onPress={() => { if (igUrl !== '#') require('react-native').Linking.openURL(igUrl); }}
                    >
                      <Ionicons name="logo-instagram" size={10} color="#94A3B8" />
                      <Text style={styles.spinSocialBtnText}>{igLabel}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.spinSocialBtn}
                      onPress={() => { if (ttUrl !== '#') require('react-native').Linking.openURL(ttUrl); }}
                    >
                      <FontAwesome5 name="tiktok" size={10} color="#94A3B8" />
                      <Text style={styles.spinSocialBtnText}>{ttLabel}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })()}

            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* ── Prize Result Modal ── */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={spinResultVisible}
        onRequestClose={() => {
          if (
            spinPrizeResult?.prize_type === 'product'
            && !spinProductClaimSuccess
          ) {
            return;
          }
          closeSpinResultAndRefresh();
        }}
      >
        <View style={styles.spinResultOverlay}>
          <View style={[styles.spinResultCard, {position: 'relative'}]}>

            {confettiWrapper}

            {/* Ribbon SELAMAT */}
            <View style={styles.spinResultRibbonWrap}>
              <PrizeSelamatBanner />
            </View>

            {spinPrizeResult?.prize_type === 'points' ? (
              /* ── POINTS RESULT ── */
              <View style={styles.spinResultContent}>
                <View style={styles.spinResultPtsRewardBlock}>
                  <Text style={styles.spinResultSubtitle}>Kamu mendapatkan</Text>
                  <PrizePointsStarRings />
                  <PrizePointsGradientLabel label={`${spinPrizeResult.points_value} poin`} />
                  <View style={styles.spinResultPtsBadge}>
                    <View style={styles.spinResultPtsBadgeIcon}>
                      <PointIcon size={16} color={"#F59E0B"} />
                    </View>
                    <Text style={styles.spinResultPtsBadgeText}>
                      +{spinPrizeResult.points_credited ?? spinPrizeResult.points_value} poin ditambahkan
                    </Text>
                  </View>
                </View>
                {spinPrizeResult.total_points_after != null ? (
                  <View style={styles.spinResultPtsTotalWrap}>
                    <Text style={styles.spinResultTotalLabel}>Total Poin Kamu sekarang</Text>
                    <View style={styles.spinResultTotalBox}>
                      <View style={styles.spinResultTotalStarChip}>
                        <PointIcon size={16} color={"#F59E0B"} />
                      </View>
                      <Text style={styles.spinResultTotalText}>
                        {formatPoinNumber(spinPrizeResult.total_points_after)} poin
                      </Text>
                    </View>
                  </View>
                ) : null}
                <View style={styles.spinResultPtsActions}>
                  <TouchableOpacity
                    style={styles.spinResultClaimBtn}
                    accessibilityRole="button"
                    accessibilityLabel="Lihat Poin"
                    onPress={() => {
                      closeSpinResultAndRefresh();
                      void closeSpinPopup();
                      navigation.navigate('TukarPoin', { tab: 'promo' });
                    }}
                  >
                    <Text style={styles.spinResultClaimBtnText}>Tukar Poin</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.spinResultCloseBtn}
                    onPress={closeSpinResultAndRefresh}
                  >
                    <Text style={styles.spinResultCloseBtnText}>Tutup</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : spinPrizeResult?.prize_type === 'none' ? (
              /* ── NON BENEFIT ── */
              <View style={styles.spinResultContent}>
                <Text style={styles.spinResultNoneMessage}>Silahkan Coba lagi</Text>
                <TouchableOpacity
                  style={styles.spinResultCloseBtn}
                  onPress={closeSpinResultAndRefresh}
                >
                  <Text style={styles.spinResultCloseBtnText}>Tutup</Text>
                </TouchableOpacity>
              </View>
            ) : (
              /* ── PRODUCT / VOUCHER RESULT ── */
              <View style={styles.spinResultContent}>
                {spinPrizeResult?.prize_type === 'product' && spinProductClaimSuccess ? (
                  <>
                    <Text style={styles.spinResultClaimOkTitle}>Klaim berhasil</Text>
                    <Text style={styles.spinResultClaimOkBody}>{spinClaimSuccessMessage}</Text>
                    {spinClaimFulfillment === 'offline_event' && spinRedemptionCode ? (
                      <View style={styles.spinRedemptionBox}>
                        <Text style={styles.spinRedemptionLabel}>Tunjukkan QR ke panitia booth / event</Text>
                        <View style={{ alignItems: 'center', marginBottom: 12 }}>
                          <QRCode
                            value={String(spinRedemptionCode)}
                            size={180}
                            color="#000000"
                            backgroundColor="#FFFFFF"
                          />
                        </View>
                        <Text style={styles.spinRedemptionLabel}>Kode redemption</Text>
                        <Text style={styles.spinRedemptionCode} selectable>
                          {spinRedemptionCode}
                        </Text>
                      </View>
                    ) : null}
                    <TouchableOpacity
                      style={[styles.spinResultCloseBtn, { marginTop: 8 }]}
                      onPress={closeSpinResultAndRefresh}
                    >
                      <Text style={styles.spinResultCloseBtnText}>Tutup</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <>
                    <Text style={styles.spinResultSubtitle}>Kamu mendapatkan</Text>
                    {spinPrizeResult?.product_image_url ? (
                      <Image
                        source={{ uri: spinPrizeResult.product_image_url }}
                        style={styles.spinResultProductImg}
                        resizeMode="contain"
                      />
                    ) : (
                      <View style={styles.spinResultProductImgPlaceholder}>
                        <Ionicons name="gift" size={64} color="#FCD34D" />
                      </View>
                    )}
                    <SpinResultProductTitle
                      displayName={getSpinProductDisplayLines(spinPrizeResult).displayName}
                    />
                    <View style={styles.spinResultInfoBox}>
                      <Ionicons name="information-circle" size={19} color="#F59E0B" />
                      <Text style={styles.spinResultInfoText}>
                        {spinPromo?.data?.product_fulfillment === 'offline_event'
                          ? 'Ambil hadiah di booth acara. Tekan Klaim untuk mendapatkan kode bukti panitia.'
                          : 'Hadiah barang wajib diklaim dalam hari ini.'}
                      </Text>
                    </View>
                    {spinPrizeResult?.prize_type === 'product' ? (
                      <TouchableOpacity
                        style={[styles.spinResultClaimBtn, spinClaimBusy && { opacity: 0.72 }]}
                        disabled={spinClaimBusy}
                        onPress={() => void claimSpinWheelProductClub()}
                      >
                        {spinClaimBusy ? (
                          <ActivityIndicator color="#FFFFFF" />
                        ) : (
                          <Text style={styles.spinResultClaimBtnText}>Klaim Hadiah Sekarang</Text>
                        )}
                      </TouchableOpacity>
                    ) : (
                      <>
                        <TouchableOpacity
                          style={styles.spinResultClaimBtn}
                          onPress={() => {
                            const url = spinPrizeResult?.product_url;
                            if (url) require('react-native').Linking.openURL(url);
                            closeSpinResultAndRefresh();
                          }}
                        >
                          <Text style={styles.spinResultClaimBtnText}>Klaim Hadiah Sekarang</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.spinResultCloseBtn, { marginTop: 12 }]}
                          onPress={closeSpinResultAndRefresh}
                        >
                          <Text style={styles.spinResultCloseBtnText}>Tutup</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </>
                )}
              </View>
            )}

          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={!!spinClaimConfirm}
        onRequestClose={() => setSpinClaimConfirm(null)}
      >
        <View style={styles.spinResultOverlay}>
          <View style={styles.spinClaimConfirmCard}>
            <Text style={styles.spinClaimConfirmTitle}>Konfirmasi pengiriman</Text>
            <Text style={styles.spinClaimConfirmSubtitle}>
              Hadiah produk akan diproses dengan alamat utama berikut:
            </Text>
            <Text style={styles.spinClaimConfirmAddr}>
              {(spinClaimConfirm?.addr?.nama_alamat || 'Utama')}
              {'\n'}
              {`${spinClaimConfirm?.addr?.nama_penerima || ''} · ${spinClaimConfirm?.addr?.no_telp || ''}`}
              {'\n'}
              {[
                spinClaimConfirm?.addr?.detail_alamat,
                spinClaimConfirm?.addr?.kecamatan,
                spinClaimConfirm?.addr?.kota,
                spinClaimConfirm?.addr?.provinsi,
                spinClaimConfirm?.addr?.kodepos,
              ]
                .filter(Boolean)
                .join(', ')}
            </Text>
            <TouchableOpacity
              style={[styles.spinResultClaimBtn, spinClaimBusy && { opacity: 0.72 }]}
              disabled={spinClaimBusy}
              onPress={() => {
                setSpinClaimConfirm((cur) => {
                  const lid = spinLastLogIdRef.current;
                  const aid = cur?.addr?.id;
                  if (lid && aid) {
                    void runSpinWheelProductClaim(lid, Number(aid));
                  }
                  return null;
                });
              }}
            >
              {spinClaimBusy ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.spinResultClaimBtnText}>Kirim ke alamat ini</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.spinResultCloseBtn, { marginTop: 10 }]}
              disabled={spinClaimBusy}
              onPress={() => {
                const aid = spinClaimConfirm?.addr?.id;
                if (aid != null && Number(aid) > 0) {
                  setSpinManageAddrId(Number(aid));
                }
              }}
            >
              <Text style={styles.spinResultCloseBtnText}>Ubah alamat utama</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.spinClaimConfirmBack]}
              disabled={spinClaimBusy}
              onPress={() => setSpinClaimConfirm(null)}
            >
              <Text style={styles.spinClaimConfirmBackText}>Kembali</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <SpinWheelShippingAddressModal
        visible={spinAddrModalVisible}
        blockDismiss
        onDismiss={() => setSpinAddrModalVisible(false)}
        onAddressSaved={onSpinShippingAddressSaved}
      />

      <SpinWheelShippingAddressModal
        visible={spinManageAddrId != null}
        editId={spinManageAddrId}
        blockDismiss={false}
        onDismiss={() => setSpinManageAddrId(null)}
        onAddressSaved={(addr) => {
          setSpinManageAddrId(null);
          if (addr && typeof addr.id === 'number') {
            setSpinClaimConfirm({ addr });
          }
        }}
      />

      {/* Custom Alert Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showWelcomeModal}
        onRequestClose={()=>{}}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={{
                ...styles.welcomeModalHeader,
                background: 'linear-gradient(113.04deg, #22D3EE -12.2%, #1E293B 26.17%)',
              }}>
              <TouchableOpacity
                style={{
                  minWidth: 16
                }}
                activeOpacity={0.8}
                onPress={closeWelcomeModal}
              >
                <Ionicons name="close" size={16} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
            <View style={styles.welcomeModalBody}>
              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <Image
                    source={require('../../assets/confetti.gif')}
                    style={{ width: '100%', maxWidth: 32, aspectRatio: 1 }}
                    resizeMode="contain"
                  />
                  <Text style={styles.welcomeModalTitle}>SELAMAT!</Text>
                  <Image
                    source={require('../../assets/confetti.gif')}
                    style={{ width: '100%', maxWidth: 32, aspectRatio: 1 }}
                    resizeMode="contain"
                  />
                </View>
                <Text style={styles.welcomeModalSubTitle}>Kamu sekarang resmi menjadi member ZOOLYFE CLUB!</Text>
              </View>

              <View style={{
                paddingHorizontal: 20
              }}>
                <Text style={styles.welcomeModalContent}>Bersiap untuk berbagai keseruan dan hadiah menarik:</Text>
                <Text style={styles.welcomeModalContent}>🎁 Voucher, points, T-shirt, HEXOHM, MOTOR hingga MOBIL!</Text>
                <Text style={styles.welcomeModalContent}>🎰 Daily Spin Wheel setiap hari</Text>
                <Text style={styles.welcomeModalContent}>🎮 Games & challenges seru</Text>
                <Text style={styles.welcomeModalContent}>💎 Dan banyak kejutan lainnya!</Text>
              </View>
              <Text style={styles.welcomeModalSubClosing}>Semakin aktif kamu di Zoolyfe Club, semakin besar hadiah yang bisa kamu dapatkan 😎🔥</Text>

              <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <Text style={styles.welcomeModalClosing}>WELCOME TO THE CLUB</Text>
                  <Image
                    source={require('../../assets/rocket.gif')}
                    style={{ width: '100%', maxWidth: 32, aspectRatio: 1 }}
                    resizeMode="contain"
                  />
                </View>
            </View>
          </View>
        </View>
      </Modal>

      {/* Empty Coupon Highlight Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={couponHighlight.visible}
        onRequestClose={()=>{}}
      >
        <View style={styles.modalOverlay}>
          <View 
            style={styles.modalCouponContainer}
          >
            <View 
              style={styles.modalCouponBody}
            >
              <TouchableOpacity
                style={styles.modalCouponClose}
                onPress={closeCouponHighlight}
              >
                <Ionicons name="close" size={16} color="#000000" />
              </TouchableOpacity>
              <img 
                src={couponHighlight.image ?? dummyImage }
                style={styles.modalCouponImage} 
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* Profile Completed Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={profileCompletedVisible}
        onRequestClose={()=>{}}
      >
        <View style={styles.profileCompletedModalOverlay}>
          <View style={styles.profileCompletedModalContainer}>
            <View style={styles.grayDecoration}></View>

            <View style={styles.profileCompletedModalIconContainer}>
              <HeroSolid.CheckBadge height="56" width="56" color="#00C16A" />
            </View>

            <View style={styles.profileCompletedModalBody}>
              <Text style={styles.profileCompletedModalTitle}>Berhasil Lengkapi Profil!</Text>
              
              {pendingQrValidationResult && pendingQrValidationResult.couponDetails && (
                <View style={styles.profileCompletedModalCouponContainer}>
                  <Image
                    source={pendingQrValidationResult.couponDetails.image}
                    style={styles.profileCompletedModalCouponImage}
                    resizeMode="cover"
                  />
                  <Text style={styles.profileCompletedModalCouponName}>{pendingQrValidationResult.couponDetails.nama_kupon}</Text>
                </View>
              )}

              {pendingQrValidationResult && (pendingQrValidationResult.rewards?.points > 0 || pendingQrValidationResult.rewards?.stamps > 0) && (
                <View style={styles.profileCompletedModalPointStampSection}>
                  
                  {pendingQrValidationResult.rewards?.points > 0 && (
                    <View style={styles.profileCompletedModalPointStampContainer}>
                      <PointIcon size={20} color={"#FF6B35"} />
                      <Text style={styles.profileCompletedModalPointStampText}>{pendingQrValidationResult.rewards?.points} {pendingQrValidationResult.rewards?.points === 1 ? `point` : `points`}</Text>
                    </View>
                  )}

                  {pendingQrValidationResult.rewards?.stamps > 0 && (
                    <View style={styles.profileCompletedModalPointStampContainer}>
                      <HeroSolid.Bookmark height="20" width="20" color="#4CAF50" />
                      <Text style={styles.profileCompletedModalPointStampText}>{pendingQrValidationResult.rewards?.stamps} {pendingQrValidationResult.rewards?.stamps === 1 ? `stamp` : `stamps`}</Text>
                    </View>
                  )}

                </View>
              )}

              {pendingQrValidationResult && pendingQrValidationResult.qrCode?.brand?.nama && (
                <Text style={styles.profileCompletedModalPointStampText}>Dari <Text style={{fontWeight: 700}}>{pendingQrValidationResult.qrCode?.brand?.nama}</Text></Text>
              )}

              <Text style={styles.profileCompletedModalCouponDescription}>
                {/*Segera klaim reward voucher <Text style={{fontWeight: 700}}>Rp 50.000</Text> dari Indomaret dan dapatkan reward lainnya*/}
                Segera klaim voucher Anda dan dapatkan rewardnya
              </Text>
            </View>

            <TouchableOpacity
              style={styles.profileCompletedModalButton}
              activeOpacity={0.7}
              onPress={handleAcceptReward}
            >
              <Text style={styles.profileCompletedModalButtonText}>Klaim Sekarang</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {paygiftBlockedModal}

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainerText: {
    fontFamily: 'Poppins',
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingTop: 10,
    paddingBottom: 30,
    position: 'relative',
  },
  headerPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.08,
  },
  patternCircle1: {
    position: 'absolute',
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: '#000000',
    top: -50,
    right: -50,
  },
  patternCircle2: {
    position: 'absolute',
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: '#000000',
    bottom: -30,
    left: -30,
  },
  statusBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 0,
  },
  statusTime: {
    color: '#000000',
    fontSize: 16,
    fontWeight: 'bold',
    // fontFamily: 'Poppins',
  },
  statusIcons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  battery: {
    width: 24,
    height: 12,
    borderWidth: 1,
    borderColor: '#000000',
    borderRadius: 2,
    marginLeft: 5,
    justifyContent: 'center',
    paddingHorizontal: 1,
  },
  batteryLevel: {
    width: '80%',
    height: '70%',
    backgroundColor: '#000000',
    borderRadius: 1,
  },
  profileSection: {
    position: 'relative',
    paddingTop: 12,
    height: 220,
    overflow: 'hidden',
  },
  decorativeElements: {
    position: 'absolute',
    top: -35,
    right: 0,
    width: 201,
    height: 203,
    opacity: 0.08,
  },
  decorativeShape: {
    position: 'absolute',
    backgroundColor: '#F8FAFC',
  },
  decorativeShape1: {
    width: 181,
    height: 163,
    borderRadius: 90,
    top: 0,
    left: 0,
  },
  decorativeShape2: {
    width: 181,
    height: 159,
    borderRadius: 90,
    top: 44,
    left: 20,
  },
  headerContent: {
    // flex: 1,
    // flexDirection: 'row',
    // justifyContent: 'space-between',
    // alignItems: 'top',
    // paddingTop: 30,
  },
  topHeaderRow: {
    width: '100%',
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  bottomHeaderRow: {
    width: '100%',
    paddingVertical: 4,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'visible',
  },
  avatarPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(248, 250, 252, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 'calc(infinity * 1px)',
    marginRight: 12,
  },
  profileTextContainer: {
    flex: 1,
    flexDirection: 'column',
    gap: 4,
    overflow: 'visible',
  },
  profileGreeting: {
    fontSize: 16,
    fontWeight: '600',
    color: '#F8FAFC',
    fontFamily: 'Poppins-SemiBold',
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statItemLabel: {
    fontSize: 12,
    color: '#94A3B8',
    fontFamily: 'Poppins',
  },
  statItemValue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#22D3EE',
    fontFamily: 'Poppins-Bold',
  },
  profileButtons: {
    flexDirection: 'row',
    gap: 8,
    overflow: 'visible',
  },
  notificationButton: {
    width: 28,
    height: 28,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
  content: {
    flex: 1,
  },
  
  cardsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 4,
    gap: 16,
  },
  pointCard: {
    flex: 1,
    backgroundColor: '#F59E0B',
    borderRadius: 12,
    padding: 8,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)',
    elevation: 8,
    width: 172,
    height: 36,
  },
  stampCard: {
    flex: 1,
    backgroundColor: '#22D3EE',
    borderRadius: 12,
    padding: 8,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)',
    elevation: 8,
    width: 172,
    height: 36,
  },
  ellipseBackground: {
    position: 'absolute',
    width: 213,
    height: 213,
    borderRadius: 106.5,
    borderWidth: 24,
    borderColor: '#FFFFFF',
    opacity: 0.24,
    top: 0,
    left: 0,
	  transform: 'translateX(-45%) translateY(-37%)',
  },
  cardContent: {
    // flex: 1,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  cardTitle: {
    color: '#F8FAFC',
    fontSize: 12,
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
    lineHeight: 16,
    textWrap: 'nowrap',
  },
  pointEllipseBackground: {
    position: 'absolute',
    width: 213,
    height: 213,
    borderRadius: 106.5,
    borderWidth: 24,
    borderColor: '#FFFFFF',
    opacity: 0.24,
    top: 0,
    left: 0,
	  transform: 'translateX(-45%) translateY(-37%)',
  },
  coinContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  coin: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pointCoinContainer: {
    width: 18,
    height: 18,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.24)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 9,
  },
  pointCoin: {
    width: 10,
    height: 10,
    borderRadius: 12,
    backgroundColor: '#FEF3C7',
    borderWidth: 2,
    borderColor: '#FBBF24',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
  },
  stampContainer: {
    width: 18,
    height: 18,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.24)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'baseline',
    alignSelf: 'stretch',
    gap: 4,
    marginTop: 36,
  },
  cardLabel: {
    color: '#F8FAFC',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Poppins',
    marginBottom: 2,
    lineHeight: 16,
  },
  pointsDisplay: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  pointsValue: {
    color: '#F8FAFC',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Poppins-SemiBold',
    marginLeft: 'auto',
    marginRight: 4,
    lineHeight: 20,
  },
  pointsUnit: {
    color: '#F8FAFC',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Poppins',
    lineHeight: 16,
  },

  bottomSpacing: {
    height: 20,
  },
  promoCornerSection: {
    paddingHorizontal: 20,
    marginTop: 12,
    gap: 12,
    paddingBottom: 100
  },
  promoHeader: {
    flexDirection: 'col',
    alignItems: 'flex-start',
    gap: 4,
    position: 'relative'
  },
  promoCornerTitle: {
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'Poppins-Semibold',
    color: '#000000',
    lineHeight: 28,
  },
  lihatSemuaButton: {
    backgroundColor: '#F1F5F9',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  lihatSemuaText: {
    fontSize: 14,
    fontWeight: '500',
    // fontFamily: 'Poppins',
    color: '#1E293B',
    lineHeight: 20,
  },
  promoSubtitle: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Poppins',
    color: '#94A3B8',
    lineHeight: 16,
  },
  promoLoadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  promoLoadingText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#94A3B8',
  },
  promoGrid: {
    gap: 12,
  },
  promoCard: {
    width: '100%',
    borderRadius: 12,
    minHeight: 145,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  },
  promoCardBackground: {
    flex: 1,
    minHeight: 145,
  },
  promoCardImage: {
    borderRadius: 12,
  },
  promoCardOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    padding: 16,
    borderRadius: 12,
  },
  promoCardContent: {
    flex: 1,
    justifyContent: 'space-between',
  },
  promoCardHeader: {
    marginBottom: 8,
  },
  promoTypeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  promoTypeText: {
    fontSize: 10,
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
  },
  promoCardTitle: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    marginBottom: 4,
    lineHeight: 18,
  },
  promoCardSubtitle: {
    fontSize: 12,
    fontFamily: 'Poppins',
    fontWeight: '400',
    opacity: 0.9,
    lineHeight: 16,
  },
  promoEmptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  promoEmptyText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#94A3B8',
    marginTop: 8,
    textAlign: 'center',
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 0,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    fontFamily: 'Poppins-SemiBold',
    color: '#000000',
  },
  closeButton: {
    padding: 4,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
  },
  validationModalContent: {
    maxHeight: height * 0.88,
    flexDirection: 'column',
    maxWidth: 360,
  },
  validationScrollView: {
    flex: 1,
    minHeight: 0,
  },
  successIconContainer: {
    marginBottom: 16,
  },
  errorIconContainer: {
    marginBottom: 16,
  },
  modalMessage: {
    fontSize: 16,
    fontFamily: 'Poppins',
    color: '#374151',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 16,
  },
  pointsEarnedContainer: {
    backgroundColor: '#10B981',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  pointsEarnedText: {
    fontSize: 16,
    fontFamily: 'Poppins-SemiBold',
    color: '#FFFFFF',
    fontWeight: '600',
  },
  modalButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonSuccess: {
    backgroundColor: '#10B981',
  },
  modalButtonError: {
    backgroundColor: '#EF4444',
  },
  modalButtonText: {
    fontSize: 16,
    fontFamily: 'Poppins-SemiBold',
    color: '#FFFFFF',
    fontWeight: '600',
  },

  // ScanScreen-like Modal Styles
  resultHeader: {
    padding: 16,
    alignItems: 'center',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
  },
  brandLogoContainer: {
    width: 60,
    height: 60,
    borderRadius: 'calc(infinity * 1px)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
    // padding: 10,
  },
  brandLogo: {
    width: 60,
    height: 60,
  },
  resultIcon: {
    width: 48,
    height: 48,
    borderRadius: 'calc(infinity * 1px)',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  resultTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    lineHeight: 24,
    fontWeight: 600,
    marginBottom: 8,
    textAlign: 'center',
    fontFamily: 'Poppins-SemiBold'
  },
  resultMessage: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    fontFamily: 'Poppins',
    fontWeight: 400,
  },
  brandName: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontWeight: '600',
  },
  resultDetails: {
    padding: 16,
  },
  qrInfoSection: {
    // marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 'bold',
    color: '#94A3B8',
    marginBottom: 8,
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
  },
  qrName: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '600',
    color: '#0F172A',
    // marginBottom: 5,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: 600,
  },
  qrDescription: {
    fontSize: 12,
    color: '#94A3B8',
    lineHeight: 16,
    marginTop: 4,
    fontFamily: 'Poppins',
  },
  rewardsSection: {
    flexDirection: 'column',
    gap: 4,
    // marginBottom: 20,
  },
  rewardItem: {
    flexDirection: 'row',
    alignItems: 'center',
    // marginBottom: 12,
  },
  rewardText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#0F172A',
    marginLeft: 6,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: 600,
  },

  couponSection: {
    // marginBottom: 15,
  },
  couponImageContainer: {
    width: '100%',
    height: 120,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 16,
    backgroundColor: '#F1F5F9',
  },
  couponImage: {
    width: '100%',
    height: '100%',
  },
  couponImagePlaceholder: {
    width: '100%',
    height: 120,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  couponName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 5,
  },
  couponDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
    lineHeight: 18,
  },
  couponDiscount: {
    fontSize: 14,
    fontWeight: 600,
    color: '#4CAF50',
    // marginBottom: 5,
    fontFamily: 'Poppins-SemiBold',
  },
  couponExpiry: {
    fontSize: 14,
    color: '#999',
    fontStyle: 'italic',
  },
  validationActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    gap: 12,
  },
  validationButton: {
    flex: 1,
    padding: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
  },
  acceptButton: {
    backgroundColor: '#00C16A',
  },
  cancelButtonText: {
    color: '#374151',
    lineHeight: 20,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium'
  },
  acceptButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium'
  },

  validationActionsVertical: {
    flexDirection: 'column',
    paddingHorizontal: 16,
    gap: 12
  },
  validationButtonWide: {
    flex: 1,
    padding: 12,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 1,
  },
  completeButton: {
    backgroundColor: '#1E293B',
    borderColor: '#F3F4F6',
  },
  stopButton: {
    backgroundColor: 'transparent',
    borderColor: '#64748B',
  },
  completeButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF'
  },
  stopButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color: '#64748B'
  },

  rewardInfo: {
    alignItems: 'center',
    marginBottom: 20,
  },
  rewardLabel: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    color: '#666',
    marginBottom: 5,
    fontFamily: 'Poppins-Medium'
  },
  rewardValueRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rewardValue: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 600,
    color: '#FF6B35',
    fontFamily: 'Poppins-SemiBold'
  },
  rewardBrandText: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 400,
    color: '#666',
    marginLeft: 4,
    fontFamily: 'Poppins'
  },
  couponInfo: {
    alignItems: 'center',
    marginBottom: 20,
  },
  couponLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  couponName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  couponCode: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FF6B35',
    textAlign: 'center',
    marginBottom: 8,
  },
  // couponDiscount: {
  //   fontSize: 20,
  //   fontWeight: 'bold',
  //   color: '#4CAF50',
  //   textAlign: 'center',
  //   marginBottom: 12,
  // },
  couponNote: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 15,
  },
  // Type 'all' reward layout (gambar 3)
  rewardAllContainer: {
    width: '100%',
    marginBottom: 20,
  },
  rewardAllSection: {
    paddingVertical: 12,
  },
  rewardAllHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  rewardAllIcon: {
    marginRight: 8,
  },
  rewardAllSectionTitle: {
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium',
    color: '#333',
  },
  rewardAllDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 4,
  },
  rewardAllVoucherName: {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Poppins-SemiBold',
    color: '#222',
    marginTop: 8,
    marginBottom: 6,
  },
  rewardAllValidityNote: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#666',
  },
  rewardAllUsage: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#666',
  },
  rewardAllStatusTitle: {
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium',
    color: '#333',
    marginBottom: 6,
  },
  rewardAllStatusValue: {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'Poppins-SemiBold',
    color: '#333',
    marginBottom: 8,
  },
  rewardAllUsageNote: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#666',
  },
  userStats: {
    backgroundColor: '#F8F9FA',
    padding: 15,
    borderRadius: 10,
    marginBottom: 15,
  },
  statsTitle: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
    fontFamily: 'Poppins-Medium',
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#333',
    marginLeft: 5,
    fontWeight: 600,
    fontFamily: 'Poppins-SemiBold',
  },
  continueButton: {
    margin: 16,
    marginTop: 0,
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 500,
    fontFamily: 'Poppins-Medium',
  },

  validationResultHeader: {
    backgroundColor: '#FFFFFF',
  },
  validationResultTitle: {
    color: '#000000'
  },
  validationResultMiniSectionTitle: {
    fontSize: 12,
    marginBottom: 4,
  },
  validationResultMiniSectionName: {
    fontSize: 12,
  },
  validationResultMiniSectionDescription: {
    fontSize: 10,
    marginTop: 2,
  },
  
  // Loading Overlay Styles
  loadingOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContent: {
    backgroundColor: '#FFFFFF',
    padding: 30,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 200,
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    textAlign: 'center',
  },
  spinModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 18,
  },
  spinModalCard: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.14,
    shadowRadius: 20,
    elevation: 10,
    overflow: 'hidden',
  },
  spinModalScroll: {
    width: '100%',
  },
  spinModalScrollContent: {
    alignItems: 'center',
    gap: 10,
    width: '100%',
    paddingBottom: 4,
  },
  spinCloseBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    padding: 4,
  },
  spinTitleGroup: {
    alignItems: 'center',
    gap: 3,
    width: '100%',
    paddingTop: 0,
    paddingHorizontal: 28,
  },
  spinTitleLogo: {
    width: 130,
    maxWidth: '70%',
    aspectRatio: 80 / 56,
    marginBottom: -4,
  },
  spinClubUrl: {
    color: '#64748B',
    fontSize: 13,
    fontWeight: '400',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  spinResultClaimOkTitle: {
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'Poppins-SemiBold',
    color: '#0F172A',
    textAlign: 'center',
    marginBottom: 10,
  },
  spinResultClaimOkBody: {
    fontSize: 14,
    fontWeight: '400',
    fontFamily: 'Poppins',
    color: '#475569',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 12,
    paddingHorizontal: 12,
  },
  spinRedemptionBox: {
    alignSelf: 'stretch',
    marginBottom: 14,
    paddingHorizontal: 10,
  },
  spinRedemptionLabel: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 8,
  },
  spinRedemptionCode: {
    fontSize: 20,
    fontWeight: '700',
    fontFamily: 'Poppins-SemiBold',
    letterSpacing: 1,
    color: '#0F172A',
    textAlign: 'center',
    backgroundColor: '#F8FAFC',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#CBD5E1',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 12,
  },
  spinHeading: {
    color: '#000000',
    fontSize: 18,
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
    textAlign: 'center',
    marginTop: -2,
  },
  spinBadge: {
    backgroundColor: '#FEF3C7',
    borderRadius: 6,
    paddingVertical: 2,
    paddingHorizontal: 6,
    maxWidth: '100%',
  },
  spinBadgeText: {
    color: '#F59E0B',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  spinBadgeSoldOut: {
    backgroundColor: '#F3F4F6',
    borderRadius: 6,
    paddingVertical: 2,
    paddingHorizontal: 6,
    maxWidth: '100%',
  },
  spinBadgeSoldOutText: {
    color: '#4B5563',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  // ── Sold-out / Tunggu Besok
  spinSoldOutBtnWrap: {
    width: '100%',
  },
  spinSoldOutBtnClip: {
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
  },
  spinSoldOutBtn: {
    width: '100%',
    borderRadius: 8,
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  spinSoldOutBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
  },
  spinCountdownWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  spinCountdownLabel: {
    fontSize: 12,
    color: '#64748B',
    fontFamily: 'Poppins',
  },
  spinCountdownTime: {
    fontSize: 12,
    color: '#F59E0B',
    fontWeight: '700',
    fontFamily: 'Poppins-Bold',
  },
  // ── Prize result modal
  spinResultOverlay: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 18,
  },
  spinResultCard: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingTop: 52,
    paddingBottom: 20,
    paddingHorizontal: 16,
    overflow: 'visible',
    position: 'relative',
    gap: 24,
  },
  spinResultRibbonWrap: {
    position: 'absolute',
    top: -40,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 2,
    paddingHorizontal: 8,
  },
  spinResultContent: {
    alignItems: 'center',
    gap: 24,
    width: '100%',
  },
  spinResultSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    color: '#64748B',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  spinResultNoneMessage: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    fontFamily: 'Poppins-SemiBold',
    textAlign: 'center',
    lineHeight: 22,
    paddingHorizontal: 8,
  },
  spinResultPtsRewardBlock: {
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  spinResultPtsTotalWrap: {
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  spinResultPtsActions: {
    width: '100%',
    gap: 12,
  },
  spinResultPtsBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFBEB',
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    gap: 8,
    maxWidth: '100%',
  },
  spinResultPtsBadgeIcon: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: '#FBBF24',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinResultPtsBadgeStar: {
    fontSize: 11,
    color: '#F59E0B',
    fontWeight: '700',
    lineHeight: 12,
  },
  spinResultPtsBadgeText: {
    fontSize: 12,
    fontWeight: '400',
    color: '#F59E0B',
    fontFamily: 'Poppins',
    flexShrink: 1,
  },
  spinResultTotalLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
    fontFamily: 'Poppins-SemiBold',
    textAlign: 'center',
    marginTop: 0,
  },
  spinResultTotalBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    gap: 8,
    alignSelf: 'center',
  },
  spinResultTotalStarChip: {
    borderWidth: 1.5,
    borderColor: '#FBBF24',
    backgroundColor: '#FEF3C7',
    borderRadius: 999,
    padding: 3,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 22,
    minHeight: 22,
  },
  spinResultTotalStarGlyph: {
    fontSize: 9,
    lineHeight: 10,
    color: '#F59E0B',
    fontWeight: '700',
  },
  spinResultTotalText: {
    fontSize: 12,
    color: '#000000',
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    flexShrink: 1,
  },
  spinPendingBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 14,
    backgroundColor: '#FFFBEB',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#FDE68A',
  },
  spinPendingBannerIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FEF3C7',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  spinPendingBannerText: {
    flex: 1,
  },
  spinPendingBannerTitle: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#92400E',
  },
  spinPendingBannerSub: {
    fontSize: 12,
    fontFamily: 'Poppins',
    color: '#B45309',
    marginTop: 2,
  },
  spinResultCloseBtn: {
    width: '100%',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#64748B',
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
    marginTop: 0,
  },
  spinResultCloseBtnText: {
    fontSize: 14,
    color: '#64748B',
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
  },
  spinResultProductImg: {
    width: '80%',
    height: 160,
    alignSelf: 'center',
  },
  spinResultProductImgPlaceholder: {
    width: 120,
    height: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinResultInfoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 6,
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    padding: 8,
    width: '100%',
  },
  spinResultInfoText: {
    flex: 1,
    fontSize: 12,
    fontWeight: '400',
    color: '#0F172A',
    fontFamily: 'Poppins',
    lineHeight: 18,
    textAlign: 'left',
  },
  spinResultClaimBtn: {
    width: '100%',
    borderRadius: 8,
    backgroundColor: '#0F172A',
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
  },
  spinResultClaimBtnText: {
    fontSize: 14,
    color: '#F8FAFC',
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
  },
  spinClaimConfirmCard: {
    width: '92%',
    maxWidth: 320,
    alignSelf: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingTop: 24,
    paddingBottom: 20,
    paddingHorizontal: 16,
    alignItems: 'stretch',
    gap: 0,
  },
  spinClaimConfirmTitle: {
    fontSize: 18,
    fontWeight: '700',
    fontFamily: 'Poppins-Bold',
    color: '#0F172A',
    textAlign: 'center',
    marginBottom: 10,
  },
  spinClaimConfirmSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    fontFamily: 'Poppins',
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 20,
  },
  spinClaimConfirmAddr: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#0F172A',
    lineHeight: 21,
    width: '100%',
    padding: 12,
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 18,
  },
  spinClaimConfirmBack: {
    alignItems: 'center',
    paddingVertical: 10,
    marginTop: 4,
  },
  spinClaimConfirmBackText: {
    fontSize: 14,
    color: '#64748B',
    fontFamily: 'Poppins-Medium',
  },
  // ── Wheel area
  spinWheelArea: {
    width: WHEEL_SIZE,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 2,
    paddingBottom: 6,
    paddingHorizontal: 6,
    overflow: 'visible',
    position: 'relative',
  },
  spinWheelOuterShadow: {
    width: WHEEL_SIZE,
    height: WHEEL_SIZE,
    borderRadius: WHEEL_SIZE / 2,
    position: 'relative',
    zIndex: 1,
    shadowColor: '#67E8F9',
    shadowOffset: { width: 6, height: 12 },
    shadowOpacity: 0.24,
    shadowRadius: 18,
    elevation: 14,
  },
  spinWheelInnerClip: {
    width: WHEEL_SIZE,
    height: WHEEL_SIZE,
    borderRadius: WHEEL_SIZE / 2,
    overflow: 'hidden',
  },
  spinCenterLogoWrap: {
    position: 'absolute',
    top: WHEEL_CX - 12,
    left: WHEEL_CX - 12,
    width: 24,
    height: 24,
    borderRadius: 12,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* Sama seperti .spinwheel-web-pointer-wrap di landing.php */
  spinPointerWrap: {
    zIndex: 10,
    marginBottom: -26,
    alignItems: 'center',
    backgroundColor: 'transparent',
    transform: [{ translateY: 4 }],
  },
  spinNowBtnWrap: {
    width: '100%',
    overflow: 'visible',
  },
  spinNowBtnTouchable: {
    width: '100%',
    borderRadius: 8,
    backgroundColor: '#1B1F25',
  },
  spinNowBtnClip: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#1B1F25',
  },
  spinNowGradient: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    width: '100%',
    alignSelf: 'stretch',
  },
  spinNowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  spinNowText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
  },
  // ── Social box
  spinSocialBox: {
    width: '100%',
    backgroundColor: '#DBEAFE',
    borderRadius: 8,
    padding: 8,
    gap: 8,
    alignItems: 'center',
  },
  spinSocialLogo: {
    width: 100,
    height: 20,
  },
  spinSocialTitle: {
    fontSize: 12,
    color: '#0F172A',
    fontWeight: '400',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  spinSocialBtns: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  spinSocialBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#e3efff',
    borderRadius: 3,
    borderWidth: 0.8,
    borderColor: '#CBD5E1',
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  spinSocialBtnText: {
    fontSize: 7.5,
    color: '#0F172A',
    fontWeight: '400',
    fontFamily: 'Poppins',
  },
  spinTermsText: {
    fontSize: 10,
    color: '#64748B',
    fontWeight: '400',
    fontFamily: 'Poppins',
    textAlign: 'center',
    lineHeight: 15,
    paddingHorizontal: 6,
  },
  spinTermsBold: {
    fontWeight: '600',
    color: '#64748B',
  },

  zclubLogoContainer: {
    position: 'relative',
    width: 130,
    marginHorizontal: 'auto',
  },
  zclubLogo: {
    width: 130,
  },
  zclubLogoBottomText: {
    width: '100%',
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: 12,
    color: '#FFFFFF',
    textAlign: 'center',
    letterSpacing: '5%',
  },
  zclubLogoIcon: {
    position: 'absolute',
    top: 0,
    right: '20%',
    width: 12,
    height: 12
  },

  welcomeModalHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 20,
    padding: 16,
    marginBottom: 0,
  },
  welcomeModalBody: {
    padding: 16,
    flexDirection: 'column',
    gap: 16,
  },
  welcomeModalTitle: {
    fontFamily: 'Poppins-SemiBold', 
    fontSize: 24, 
    fontWeight: 600, 
    color: '#1E293B', 
    textAlign: 'center',
  },
  welcomeModalSubTitle: {
    fontFamily: 'Poppins-SemiBold', 
    fontSize: 16, 
    fontWeight: 600, 
    color: '#1E293B', 
    textAlign: 'center'
  },
  welcomeModalContent: {
    fontFamily: 'Poppins', 
    fontSize: 14, 
    fontWeight: 400, 
    color: '#1E293B', 
    textAlign: 'left'
  },
  welcomeModalSubClosing: {
    fontFamily: 'Poppins-Medium', 
    fontSize: 14, 
    fontWeight: 500, 
    color: '#1E293B', 
    textAlign: 'center'
  },
  welcomeModalClosing: {
    fontFamily: 'Poppins-Bold', 
    fontSize: 18, 
    fontWeight: 700, 
    color: '#1E293B', 
    textAlign: 'center'
  },

  promoHeaderButton: {
    position: 'absolute',
    top: -12,
    right: -20,
    minWidth: 110,
  },

  couponCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    elevation: 3,
  },
  couponCardExpired: {
    backgroundColor: '#F9FAFB',
    opacity: 0.7,
  },
  couponHeader: {
    height: 145,
    backgroundColor: '#F1F5F9',
  },
  couponImagePlaceholder: {
    flex: 1,
    backgroundColor: '#E2E8F0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  couponHeaderImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  couponContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  couponBadges: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6,
  },
  couponTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#000000',
    fontFamily: 'Poppins-SemiBold',
    lineHeight: 16,
  },
  couponButton: {
    backgroundColor: '#1E293B',
    borderRadius: 6,
    padding: 8,
  },
  couponButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
    fontFamily: 'Poppins-Medium',
    lineHeight: 16,
  },

  modalCouponContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 0,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
    height: 533,
  },
  modalCouponBody: {
    position: 'relative',
    height: 533,
  },
  modalCouponClose: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 24,
    height: 24,
    marginTop: 8,
    marginRight: 8,
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 'calc(infinity * 1px)',
  },
  modalCouponImage: {
    height: '100%',
    objectFit: 'cover'
  },

  bannerContainer: {
    width: '100%',
    padding: 16,
    paddingTop: 0,
  },
  banner: {
    position: 'relative',
    width: '100%',
    borderRadius: 6,
    overflow: 'clip',
  },
  bannerImage: {
    width: '100%',
    aspectRatio: 104/30,
  },
  bannerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  bannerContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  bannerLogo: {
    marginBottom: 8,
    minHeight: 16,
    height: 16,
    width: 50,
  },
  bannerTitle: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#ffffff',
  },
  bannerButtonContainer: {
    marginTop: 6,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8
  },
  bannerButton: {
    padding: 4,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 6,
  },
  bannerButtonIcon: {
    marginRight: 4,
    color: '#ffffff',
    fontSize: 12,
  },
  bannerButtonText: {
    fontFamily: 'Poppins',
    fontSize: 9,
    color: '#ffffff',
  },
  bannerLoadingText: {
    paddingTop: 10,
    paddingBottom: 10,
    width: '100%',
    textAlign: 'center',
  },

  horizontalLine: {
    width: '100%',
    height: 1,
    backgroundColor: "#F1F5F9",
    marginVertical: 16,
  },
  horizontalDashedLine: {
    // width: '100%',
    height: 0,
    borderTopWidth: 1,
    borderStyle: 'dashed',
    borderColor: "#000000",
    marginVertical: 16,
  },

  couponDetailSection: {
    flexDirection: 'row',
    borderRadius: 6,
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: '#00C16A',
    overflow: 'clip',
  },
  couponDetailIcon: {
    backgroundColor: '#00C16A',
    paddingHorizontal: 12,
    paddingVertical: 16,
  },
  couponDetailDescription: {
    flexDirection: 'column',
    padding: 8,
  },
  couponDetailTitle: {
    color: '#00C16A',
    fontFamily: 'Poppins-SemiBold',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 20,
  },
  couponDetailSubtitle: {
    color: '#64748B',
    fontFamily: 'Poppins',
    fontWeight: 300,
    fontStyle: 'italic',
    fontSize: 12,
    lineHeight: 16,
  },

  profileCompletedModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
  },
  profileCompletedModalContainer: {
    flexDirection: 'column',
    gap: 16,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    width: '100%',
    maxWidth: 360,
    padding: 16,
    borderRadius: 24,
  },
  profileCompletedModalIconContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: 88,
    height: 88,
    backgroundColor: '#EFFDF5',
    borderRadius: 'calc(infinity * 1px)',
  },
  profileCompletedModalBody: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    // paddingVertical: 12,
  },
  profileCompletedModalTitle: {
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 24,
    color:'#000000',
  },
  profileCompletedModalCouponContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
  },
  profileCompletedModalCouponImage: {
    width: 150, 
    aspectRatio: 2.69
  },
  profileCompletedModalCouponName: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color:'#475569',
  },
  profileCompletedModalCouponDescription: {
    paddingHorizontal: 24,
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 20,
    color:'#64748B',
    textAlign: 'center',
  },
  profileCompletedModalButton: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    padding: 12,
    backgroundColor: '#1E293B',
    borderRadius: 6,
  },
  profileCompletedModalButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color:'#FFFFFF',
    textAlign: 'center',
  },
  profileCompletedModalPointStampSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  profileCompletedModalPointStampContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  profileCompletedModalPointStampText: {
    fontFamily: 'Poppins-SemiBold',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 20,
    color: '#475569'
  },

  grayDecoration: {
    width: 40,
    height: 0,
    borderWidth: 3,
    borderColor: '#CBD5E1',
    borderRadius: 'calc(infinity * 1px)',
  },

  expiredContainer: {
    paddingBottom: 0
  },
  expiredText: {
    textAlign: 'center'
  },
  expiredDateText: {
    fontSize: 16
  },

  pointWrapper: {
    gap: 1
  },
  pointConvertion: {
    fontSize: 12,
    lineHeight: 16,
    color: '#0F172A',
    marginLeft: 6,
    fontFamily: 'Poppins',
    fontWeight: 400,
  },

  resultHeaderContainer: {
    width: '100%', 
    flexDirection: 'row', 
    gap: 16, 
    padding: 16,
    alignItems: 'center',
  },
  resultTitleWrapper: {
    flex: 1
  },
  resultTitleLeft: {
    textAlign: 'left', 
    marginBottom: 0,
  },
  resultMessageLeft: {
    textAlign: 'left', 
  },
  bannerWrapper: {
    width: '100%',
  },
});
